import { Component, OnInit, OnDestroy, Input, ViewChild, ElementRef, AfterViewInit, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { RegisterClass, UUIDsEqual } from '@memberjunction/global';
import { BaseResourceComponent, NavigationService } from '@memberjunction/ng-shared';
import { ResourceData } from '@memberjunction/core-entities';
import { RunView, Metadata } from '@memberjunction/core';
import { BCMembershipService } from '@bluecypress/bcsaas-entities-extended';
import { BC_ROLE_LEVELS } from '@bluecypress/bcsaas-core';
import { Subject } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';
import { IzzyChannelMessageEntity, IzzyMetadataEngine } from 'mj_generatedentities';
import { mjBizAppsIssuesIssueEntity, mjBizAppsIssuesIssueStatusEntity, mjBizAppsIssuesIssueTypeEntity } from '@mj-biz-apps/issues-entities';
import { BCSaaSChannelEntity, BCSaaSChannelRunEntity } from '@bluecypress/bcsaas-entities';
import { BCSaaSOrganizationPersonRoleEntity } from '@bluecypress/bcsaas-entities';
import { mjBizAppsCommonOrganizationEntity, mjBizAppsCommonPersonEntity } from '@mj-biz-apps/common-entities';
import { MJUserSettingEntity } from '@memberjunction/core-entities';
import * as d3 from 'd3';
import { IzzyContextService } from '../../services/izzy-context.service';

/**
 * Time range options for analytics
 */
type TimeRange = 'today' | '7d' | '30d' | '90d' | 'all' | 'custom';

/**
 * Analytics section definition (for left nav)
 */
interface AnalyticsSection {
  id: string;
  label: string;
  icon: string;
  description: string;
}

/** A category → count row for the Tickets-section horizontal bar charts. */
interface TicketBreakdown {
  label: string;
  value: number;
}

/** Per-assignee ticket workload row (mirrors AssignmentSummary on the message side). */
interface TicketAssignmentSummary {
  personId: string | null;   // null = unassigned
  personName: string;
  count: number;             // total tickets assigned in the period
  percentage: number;
  openCount: number;         // not closed-out (active backlog)
  resolvedCount: number;     // closed-out (Resolved or terminal)
}

/**
 * KPI Card data structure
 */
interface KPICard {
  title: string;
  value: number | string;
  rawValue: number;
  previousValue?: number;
  change?: number;
  changeLabel?: string;
  changeIsPercent?: boolean;
  icon: string;
  color: string;
  trend?: 'up' | 'down' | 'flat';
  trendPercent?: number;
}

/**
 * Chart data point for line chart with multiple series
 */
interface ChartDataPoint {
  label: string;
  date: Date;
  value: number;        // Total messages
  approved: number;     // Approved messages
  avgResponseTime: number; // Average response time in minutes
}

/**
 * Chart series toggle state
 */
interface ChartSeriesState {
  total: boolean;
  approved: boolean;
  responseTime: boolean;
}

/**
 * User analytics settings persisted in MJ User Settings
 * Note: SLA settings are now stored as org-level settings, not user settings
 */
interface AnalyticsSettings {
  timeRange: TimeRange;
  customStartDate: string | null;
  customEndDate: string | null;
  organizationId: string | null;
  chartSeries: ChartSeriesState;
  isNavCollapsed: boolean;
  selectedSection: string;
}

/**
 * SLA settings resolved from org settings
 */
interface SLASettings {
  enabled: boolean;
  targetMinutes: number;
  warningThresholdPercent: number;
  businessHoursOnly: boolean;
  businessHoursStart: string;
  businessHoursEnd: string;
}

/**
 * Status breakdown item
 */
interface StatusBreakdown {
  displayPercentage: string;
  status: string;
  count: number;
  percentage: number;
  color: string;
}

/**
 * Channel performance data
 */
interface ChannelPerformance {
  channelId: string;
  channelName: string;
  totalMessages: number;
  generated: number;
  failed: number;
  pending: number;
  avgResponseTime: number;
  successRate: number;
}

/**
 * Top sender data
 */
interface TopSender {
  sender: string;
  count: number;
  percentage: number;
}

/**
 * Heatmap cell data
 */
interface HeatmapCell {
  day: number;      // 0-6 (Sun-Sat)
  hour: number;     // 0-23
  count: number;
  dayLabel: string;
  hourLabel: string;
}

/**
 * Feedback by channel breakdown
 */
interface FeedbackByChannel {
  channelId: string;
  channelName: string;
  thumbsUp: number;
  thumbsDown: number;
  total: number;
  thumbsUpPercent: number;
}

/**
 * Feedback trend data point
 */
interface FeedbackTrendPoint {
  label: string;
  date: Date;
  thumbsUp: number;
  thumbsDown: number;
  total: number;
  thumbsUpPercent: number;
}

/**
 * Assignment summary per team member
 */
interface AssignmentSummary {
  contactId: string | null;  // null = unassigned (or Izzy — see isIzzy)
  contactName: string;
  count: number;
  percentage: number;
  pendingCount: number;
  escalatedCount: number;
  approvedCount: number;
  /** True for the synthetic "Izzy" row: Approved messages with no assignee were handled
   *  end-to-end by the AI (human approvals auto-assign to the approver as of 2026-08-24). */
  isIzzy?: boolean;
}

/**
 * Category breakdown item
 */
interface CategoryBreakdown {
  category: string;
  count: number;
  percentage: number;
  displayPercentage: string;
  color: string;
  cloudFontSize: number;
}

/**
 * Drill-down filter configuration
 */
interface DrillDownFilter {
  type: 'kpi' | 'generation-status' | 'approval-status' | 'channel' | 'date' | 'sender' | 'heatmap' | 'category';
  title: string;
  value: string;
  icon: string;
  color: string;
  filterFn: (m: IzzyChannelMessageEntity) => boolean;
}

/**
 * Analytics Dashboard Component
 *
 * Comprehensive analytics dashboard with:
 * - Collapsible left navigation (Overview, Performance, Activity)
 * - KPI cards with period-over-period comparison
 * - Message volume trends
 * - Peak hours heatmap
 * - Top senders
 * - SLA tracking
 * - Channel comparison
 * - CSV export
 */
@Component({
  standalone: false,
  selector: 'izzy-analytics',
  templateUrl: './analytics.component.html',
  styleUrls: ['./analytics.component.css']
})
export class AnalyticsComponent implements OnInit, OnDestroy, AfterViewInit {
  /**
   * Optional navigation configuration passed from parent resource component
   */
  @Input() navigationConfig: Record<string, unknown> | null = null;

  @ViewChild('lineChartContainer') lineChartContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('heatmapContainer') heatmapContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('channelComparisonContainer') channelComparisonContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('ticketStatusContainer') ticketStatusContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('ticketTypeContainer') ticketTypeContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('ticketChannelContainer') ticketChannelContainer?: ElementRef<HTMLDivElement>;

  constructor(
    private navigationService: NavigationService,
    private route: ActivatedRoute,
    private location: Location,
    private contextService: IzzyContextService
  ) {}

  private chartRendered = false;
  private userSettingsKey = 'Izzy.Analytics';
  private userSetting: MJUserSettingEntity | null = null;

  // ========== Left Nav State ==========
  protected sections: AnalyticsSection[] = [
    { id: 'overview', label: 'Overview', icon: 'fa-chart-pie', description: 'Key metrics at a glance' },
    { id: 'performance', label: 'Performance', icon: 'fa-gauge-high', description: 'SLA tracking and response times' },
    { id: 'tickets', label: 'Tickets', icon: 'fa-ticket', description: 'Ticket volume, resolution & SLA' },
    { id: 'activity', label: 'Activity', icon: 'fa-clock', description: 'Peak hours and top senders' },
    { id: 'feedback', label: 'Feedback', icon: 'fa-thumbs-up', description: 'Staff quality ratings for AI responses' },
    { id: 'categories', label: 'Categories', icon: 'fa-tags', description: 'Message topic distribution' }
  ];
  protected selectedSection: AnalyticsSection = this.sections[0];
  protected isNavCollapsed = true; // Collapsed by default

  // ========== Tickets section state (§13) ==========
  /** Raw data loaded once (mirrors allMessages); derived fields computed in computeTicketAnalytics. */
  private allTickets: mjBizAppsIssuesIssueEntity[] = [];
  private allSlaStates: { IssueID: string; FirstResponseAt: Date | null; Breached: boolean; WarningFiredAt: Date | null }[] = [];
  private issueStatusById = new Map<string, { name: string; isTerminal: boolean }>();
  private issueTypeNameById = new Map<string, string>();
  /** Headline ticket KPIs for the period. */
  protected ticketKpis = {
    created: 0,
    resolved: 0,
    backlog: 0,
    avgResolutionHours: null as number | null,
    avgFirstResponseHours: null as number | null,
    slaCompliancePct: null as number | null,
  };
  /** Category → count breakdowns rendered as horizontal bars. */
  protected ticketsByStatus: TicketBreakdown[] = [];
  protected ticketsByType: TicketBreakdown[] = [];
  protected ticketsByChannel: TicketBreakdown[] = [];
  /** Per-assignee ticket workload (total + open/resolved), mirrors message Team Workload. */
  protected ticketAssignmentSummaries: TicketAssignmentSummary[] = [];
  protected totalTickets = 0;
  protected totalTicketsAssigned = 0;
  protected totalTicketsUnassigned = 0;
  protected maxTicketAssignmentCount = 0;
  /** SLA split: met / near-breach / breached counts. */
  protected slaSplit = { met: 0, nearBreach: 0, breached: 0 };
  protected reopenRatePct: number | null = null;
  protected ticketsLoaded = false;

  // Chart series toggle state
  protected chartSeries: ChartSeriesState = {
    total: true,
    approved: true,
    responseTime: true
  };

  // Response time stats
  protected avgResponseTimeMinutes = 0;
  protected minResponseTimeMinutes = 0;
  protected maxResponseTimeMinutes = 0;

  // SLA Configuration (loaded from org settings)
  protected slaSettings: SLASettings = {
    enabled: true,
    targetMinutes: 10,
    warningThresholdPercent: 80,
    businessHoursOnly: false,
    businessHoursStart: '09:00',
    businessHoursEnd: '17:00'
  };
  protected slaMetPercent = 0;
  protected slaMetCount = 0;
  protected slaMissedCount = 0;

  // Time range filter
  protected selectedTimeRange: TimeRange = '7d';
  protected timeRanges: { value: TimeRange; label: string }[] = [
    { value: 'today', label: 'Today' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: 'all', label: 'All Time' }
  ];
  protected showDatePicker = false;
  protected customStartDate: string | null = null;
  protected customEndDate: string | null = null;

  // Organization filter
  protected organizations: mjBizAppsCommonOrganizationEntity[] = [];
  protected selectedOrganizationId: string | null = null;
  protected selectedOrg: mjBizAppsCommonOrganizationEntity | null = null;

  // Organization selection follows the global header switcher via IzzyContextService

  // Loading states
  protected isInitializing = true; // True until user settings and initial data are loaded
  protected isLoading = true;
  protected isRefreshing = false;

  // KPI Cards
  protected kpiCards: KPICard[] = [];

  // Message volume trend (for chart)
  protected messageVolumeTrend: ChartDataPoint[] = [];
  protected maxMessageVolume = 0;

  // Generation status breakdown
  protected generationStatusBreakdown: StatusBreakdown[] = [];

  // Approval status breakdown
  protected approvalStatusBreakdown: StatusBreakdown[] = [];

  // Channel performance
  protected channelPerformance: ChannelPerformance[] = [];

  // Recent messages
  protected recentMessages: IzzyChannelMessageEntity[] = [];

  // Recent channel runs
  protected recentRuns: BCSaaSChannelRunEntity[] = [];

  // Top senders
  protected topSenders: TopSender[] = [];

  // Peak hours heatmap
  protected heatmapData: HeatmapCell[] = [];
  protected maxHeatmapValue = 0;

  // Quality Feedback
  protected feedbackTotalUp = 0;
  protected feedbackTotalDown = 0;
  protected feedbackTotalRated = 0;
  protected feedbackThumbsUpPercent = 0;
  protected feedbackByChannel: FeedbackByChannel[] = [];
  protected feedbackTrend: FeedbackTrendPoint[] = [];
  protected maxFeedbackTrendValue = 0;
  protected feedbackTrendMidpoint = 0;

  // Category analytics
  protected categoryBreakdown: CategoryBreakdown[] = [];
  protected totalCategorized = 0;
  protected totalUncategorized = 0;

  // Assignment analytics (admin/owner only)
  protected isCurrentUserAdmin = false;
  protected orgTeamMembers: { contactId: string; name: string }[] = [];
  /** Team Workload card mode: 'open' = current pending/escalated load (the original view);
   *  'handled' = approvals in the period per person, plus the Izzy row (AI-handled). */
  protected workloadMode: 'open' | 'handled' = 'open';
  private openWorkloadSummaries: AssignmentSummary[] = [];
  private handledWorkloadSummaries: AssignmentSummary[] = [];
  private maxOpenWorkloadCount = 0;
  private maxHandledWorkloadCount = 0;
  protected get assignmentSummaries(): AssignmentSummary[] {
    return this.workloadMode === 'open' ? this.openWorkloadSummaries : this.handledWorkloadSummaries;
  }
  protected setWorkloadMode(mode: 'open' | 'handled'): void {
    this.workloadMode = mode;
  }
  protected totalActiveMessages = 0;
  protected totalAssigned = 0;
  protected totalUnassigned = 0;
  protected get maxAssignmentCount(): number {
    return this.workloadMode === 'open' ? this.maxOpenWorkloadCount : this.maxHandledWorkloadCount;
  }

  // Drill-down state
  protected showDrillDown = false;
  protected drillDownFilter: DrillDownFilter | null = null;
  protected drillDownMessages: IzzyChannelMessageEntity[] = [];

  // Raw data
  private allMessages: IzzyChannelMessageEntity[] = [];
  private allRuns: BCSaaSChannelRunEntity[] = [];
  private channels: BCSaaSChannelEntity[] = [];

  // Previous period data for comparison
  private previousPeriodMessages: IzzyChannelMessageEntity[] = [];

  private destroy$ = new Subject<void>();

  // ========== Deep Linking State ==========
  private _pendingOrganizationId: string | null = null;
  private _pendingTimeRange: TimeRange | null = null;
  private _pendingSection: string | null = null;

  async ngOnInit(): Promise<void> {
    // Load user settings first
    await this.loadUserSettings();

    // Check for navigation configuration from parent resource (takes precedence)
    this.handleNavigationConfiguration();

    // Subscribe to query param changes for deep linking and bookmarking
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.handleQueryParams(params);
    });

    await this.loadData();

    // Apply any pending selections after data loads
    this.applyPendingSelections();

    // Publish the org this section actually resolved to the shared context so the header
    // switcher shows what the page loaded. On a hard refresh the switcher boots first and seeds
    // "first org"; the old "only if empty" guard then let this section's URL/settings-driven org
    // differ from the pill indefinitely. The service's no-op setter prevents echo loops.
    if (this.selectedOrganizationId) {
      this.contextService.selectedOrganizationId = this.selectedOrganizationId;
    }

    // React to org changes made in other Izzy sections while this tab stays alive.
    // skip(1) ignores the BehaviorSubject replay — the initial selection above
    // already consulted the context.
    this.contextService.orgChanges$
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe(orgId => this.onContextOrgChanged(orgId));

    // Initialization complete - safe to render
    this.isInitializing = false;
  }

  /**
   * Apply an org change that originated in another Izzy section.
   * A null context is ignored — null means "All Organizations", which only
   * this section can select (the other sections require a concrete org).
   */
  private onContextOrgChanged(orgId: string | null): void {
    if (!orgId || UUIDsEqual(orgId, this.selectedOrganizationId)) {
      return;
    }
    const org = this.organizations.find(o => UUIDsEqual(o.ID, orgId));
    if (org) {
      void this.onOrgSelectorChange(org, false);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    // Charts will be rendered when data finishes loading
  }

  // ========== Left Nav Methods ==========

  toggleNav(): void {
    this.isNavCollapsed = !this.isNavCollapsed;
    this.saveUserSettings();
  }

  selectSection(section: AnalyticsSection): void {
    this.selectedSection = section;
    this.saveUserSettings();
    this.updateQueryParams();

    // Render section-specific charts after a tick
    setTimeout(() => {
      if (section.id === 'overview') {
        this.renderLineChart();
      } else if (section.id === 'activity') {
        this.renderHeatmap();
      } else if (section.id === 'performance') {
        this.renderChannelComparisonChart();
      } else if (section.id === 'tickets') {
        this.renderTicketCharts();
      }
    }, 0);
  }

  // ========== Navigation Configuration & Deep Linking ==========

  private handleNavigationConfiguration(): void {
    const config = this.navigationConfig;
    if (!config) return;

    if (config['organizationId']) {
      this._pendingOrganizationId = config['organizationId'] as string;
    }
    if (config['timeRange']) {
      this._pendingTimeRange = config['timeRange'] as TimeRange;
    }
    if (config['section']) {
      this._pendingSection = config['section'] as string;
    }
  }

  private handleQueryParams(params: Record<string, string>): void {
    if (params['organizationId'] && !this._pendingOrganizationId) {
      this._pendingOrganizationId = params['organizationId'];
    }
    if (params['timeRange'] && !this._pendingTimeRange) {
      const range = params['timeRange'] as TimeRange;
      if (['today', '7d', '30d', '90d', 'all'].includes(range)) {
        this._pendingTimeRange = range;
      }
    }
    if (params['section'] && !this._pendingSection) {
      this._pendingSection = params['section'];
    }

    if (!this.isLoading && this.organizations.length > 0) {
      this.applyPendingSelections();
    }
  }

  private applyPendingSelections(): void {
    let needsRefresh = false;

    // Org selection lives in the global header switcher; without a deep-link
    // or shared-context org, follow the first org rather than an aggregate view.
    if (!this._pendingOrganizationId && !this.selectedOrganizationId && this.organizations.length > 0) {
      this._pendingOrganizationId = this.contextService.selectedOrganizationId ?? this.organizations[0].ID;
    }

    if (this._pendingOrganizationId) {
      const org = this.organizations.find(o => o.ID === this._pendingOrganizationId);
      if (org && this.selectedOrganizationId !== this._pendingOrganizationId) {
        this.selectedOrganizationId = this._pendingOrganizationId;
        needsRefresh = true;
      }
      this._pendingOrganizationId = null;
    }

    if (this._pendingTimeRange && this._pendingTimeRange !== this.selectedTimeRange) {
      this.selectedTimeRange = this._pendingTimeRange;
      needsRefresh = true;
      this._pendingTimeRange = null;
    }

    if (this._pendingSection) {
      const section = this.sections.find(s => s.id === this._pendingSection);
      if (section) {
        this.selectedSection = section;
      }
      this._pendingSection = null;
    }

    if (needsRefresh) {
      this.computeAnalytics();
    }

    this.updateQueryParams();
  }

  private updateQueryParams(): void {
    const params = new URLSearchParams();

    if (this.selectedOrganizationId) {
      params.set('organizationId', this.selectedOrganizationId);
    }
    if (this.selectedTimeRange && this.selectedTimeRange !== '7d') {
      params.set('timeRange', this.selectedTimeRange);
    }
    if (this.selectedSection.id !== 'overview') {
      params.set('section', this.selectedSection.id);
    }

    const path = this.location.path().split('?')[0];
    const queryString = params.toString();
    const newUrl = queryString ? `${path}?${queryString}` : path;
    this.location.replaceState(newUrl);
  }

  // ========== User Settings Persistence ==========

  private async loadUserSettings(): Promise<void> {
    try {
      const md = new Metadata();
      const currentUserId = md.CurrentUser.ID;

      const rv = new RunView();
      const result = await rv.RunView<MJUserSettingEntity>({
        EntityName: 'MJ: User Settings',
        ExtraFilter: `Setting = '${this.userSettingsKey}' AND UserID = '${currentUserId}'`,
        ResultType: 'entity_object'
      });

      if (result?.Success && result.Results.length > 0) {
        this.userSetting = result.Results[0];
        const settings = JSON.parse(this.userSetting.Value || '{}') as AnalyticsSettings;

        if (!this._pendingTimeRange && settings.timeRange) {
          this.selectedTimeRange = settings.timeRange;
        }
        if (settings.customStartDate) this.customStartDate = settings.customStartDate;
        if (settings.customEndDate) this.customEndDate = settings.customEndDate;
        if (settings.chartSeries) {
          this.chartSeries = settings.chartSeries;
        }
        if (settings.isNavCollapsed !== undefined) {
          this.isNavCollapsed = settings.isNavCollapsed;
        }
        if (settings.selectedSection && !this._pendingSection) {
          const section = this.sections.find(s => s.id === settings.selectedSection);
          if (section) {
            this.selectedSection = section;
          }
        }
        // SLA settings are now loaded from org settings, not user settings
      }
    } catch (error) {
      console.error('Error loading user settings:', error);
    }

    // Shared context (org picked in another Izzy section) beats this section's own
    // saved settings — and must be consulted even when no settings record exists yet.
    if (!this._pendingOrganizationId && this.contextService.selectedOrganizationId) {
      this._pendingOrganizationId = this.contextService.selectedOrganizationId;
    }
  }

  private async saveUserSettings(): Promise<void> {
    try {
      const md = new Metadata();
      const settings: AnalyticsSettings = {
        timeRange: this.selectedTimeRange,
        customStartDate: this.customStartDate,
        customEndDate: this.customEndDate,
        organizationId: this.selectedOrganizationId,
        chartSeries: this.chartSeries,
        isNavCollapsed: this.isNavCollapsed,
        selectedSection: this.selectedSection.id
      };

      if (!this.userSetting) {
        this.userSetting = await md.GetEntityObject<MJUserSettingEntity>('MJ: User Settings');
        this.userSetting.Setting = this.userSettingsKey;
        this.userSetting.UserID = md.CurrentUser.ID;
      }

      this.userSetting.Value = JSON.stringify(settings);
      await this.userSetting.Save();
    } catch (error) {
      console.error('Error saving user settings:', error);
    }
  }

  onToggleChartSeries(series: keyof ChartSeriesState): void {
    this.chartSeries[series] = !this.chartSeries[series];
    this.renderLineChart();
    this.saveUserSettings();
  }

  async loadData(): Promise<void> {
    this.isLoading = true;

    try {
      await IzzyMetadataEngine.Instance.Config(false);

      // SECURITY FIX: Only load organizations the current user has access to
      this.organizations = IzzyMetadataEngine.Instance.GetCurrentUserOrganizations();
      this.channels = IzzyMetadataEngine.Instance.Channels as BCSaaSChannelEntity[];

      // No orgs = nothing to load
      if (this.organizations.length === 0) {
        this.isLoading = false;
        return;
      }

      // Set selected org from selectedOrganizationId
      if (this.selectedOrganizationId) {
        this.selectedOrg = this.organizations.find(o => o.ID === this.selectedOrganizationId) || null;
      }

      // Load SLA settings from org settings
      this.loadSLASettings();

      // Detect admin role and load team members for assignment analytics
      await this.loadAdminContext();

      await Promise.all([
        this.loadMessages(),
        this.loadChannelRuns(),
        this.loadTickets()
      ]);

      this.computeAnalytics();
    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      this.isLoading = false;
    }
  }

  async refresh(): Promise<void> {
    this.isRefreshing = true;
    try {
      await Promise.all([
        this.loadMessages(),
        this.loadChannelRuns(),
        this.loadTickets()
      ]);
      this.computeAnalytics();
    } finally {
      this.isRefreshing = false;
    }
  }

  private async loadMessages(): Promise<void> {
    const rv = new RunView();
    const filters = this.buildMessageFilters();

    const result = await rv.RunView<IzzyChannelMessageEntity>({
      EntityName: 'Channel Messages',
      ExtraFilter: filters || undefined,
      OrderBy: 'ReceivedAt DESC',
      ResultType: 'entity_object'
    });

    this.allMessages = result?.Success ? result.Results : [];
  }

  private async loadChannelRuns(): Promise<void> {
    const rv = new RunView();
    const filters = this.buildRunFilters();

    const result = await rv.RunView<BCSaaSChannelRunEntity>({
      EntityName: 'BC: Channel Runs',
      ExtraFilter: filters || undefined,
      OrderBy: 'StartedAt DESC',
      ResultType: 'entity_object'
    });

    this.allRuns = result?.Success ? result.Results : [];
  }

  /**
   * Load the ticket data for the §13 Tickets section (mirrors loadMessages): all Issues, their SLA
   * states, and the status/type lookups. Time + org filtering happens in computeTicketAnalytics off
   * these raw arrays, exactly like messages. Issues are org-scoped via their linked ChannelMessages
   * (an Issue carries no OrganizationID), resolved in the compute step from the already-loaded messages.
   */
  private async loadTickets(): Promise<void> {
    const rv = new RunView();
    const [issueRes, statusRes, typeRes, slaRes] = await rv.RunViews([
      { EntityName: 'MJ_BizApps_Issues: Issues', ResultType: 'entity_object' },
      { EntityName: 'MJ_BizApps_Issues: Issue Status', ResultType: 'entity_object' },
      { EntityName: 'MJ_BizApps_Issues: Issue Types', ResultType: 'entity_object' },
      { EntityName: 'Ticket SLA States', ResultType: 'entity_object' },
    ]);

    this.allTickets = (issueRes?.Success ? issueRes.Results : []) as mjBizAppsIssuesIssueEntity[];

    this.issueStatusById.clear();
    for (const s of (statusRes?.Success ? statusRes.Results : []) as mjBizAppsIssuesIssueStatusEntity[]) {
      this.issueStatusById.set(s.ID, { name: s.Name, isTerminal: !!s.IsTerminal });
    }
    this.issueTypeNameById.clear();
    for (const t of (typeRes?.Success ? typeRes.Results : []) as mjBizAppsIssuesIssueTypeEntity[]) {
      this.issueTypeNameById.set(t.ID, t.Name);
    }
    this.allSlaStates = ((slaRes?.Success ? slaRes.Results : []) as { IssueID: string; FirstResponseAt: Date | null; Breached: boolean; WarningFiredAt: Date | null }[])
      .map(s => ({ IssueID: s.IssueID, FirstResponseAt: s.FirstResponseAt, Breached: s.Breached, WarningFiredAt: s.WarningFiredAt }));
  }

  /**
   * Build SQL filter for messages based on selected time range and organization.
   * Includes previous period data for trend comparisons.
   */
  private buildMessageFilters(): string | null {
    const filters: string[] = [];

    // Date filter - include previous period for trend comparison
    const dateFilter = this.buildDateFilter();
    if (dateFilter) {
      filters.push(`ReceivedAt >= '${dateFilter.toISOString()}'`);
    }

    // Organization filter - SECURITY: Always filter by user-accessible organizations
    if (this.selectedOrganizationId) {
      // Specific organization selected
      const orgChannels = this.channels
        .filter(c => c.OrganizationID === this.selectedOrganizationId)
        .map(c => `'${c.ID}'`)
        .join(',');

      if (orgChannels) {
        filters.push(`ChannelID IN (${orgChannels})`);
      }
    } else {
      // "All Organizations" - filter by user's accessible organizations only
      const userOrgIds = this.organizations.map(o => o.ID);
      const userChannels = this.channels
        .filter(c => userOrgIds.includes(c.OrganizationID))
        .map(c => `'${c.ID}'`)
        .join(',');

      if (userChannels) {
        filters.push(`ChannelID IN (${userChannels})`);
      }
    }

    return filters.length > 0 ? filters.join(' AND ') : null;
  }

  /**
   * Build SQL filter for channel runs based on selected time range and organization.
   */
  private buildRunFilters(): string | null {
    const filters: string[] = [];

    // Date filter - include previous period for trend comparison
    const dateFilter = this.buildDateFilter();
    if (dateFilter) {
      filters.push(`StartedAt >= '${dateFilter.toISOString()}'`);
    }

    // Organization filter - SECURITY: Always filter by user-accessible organizations
    if (this.selectedOrganizationId) {
      // Specific organization selected
      const orgChannels = this.channels
        .filter(c => c.OrganizationID === this.selectedOrganizationId)
        .map(c => `'${c.ID}'`)
        .join(',');

      if (orgChannels) {
        filters.push(`ChannelID IN (${orgChannels})`);
      }
    } else {
      // "All Organizations" - filter by user's accessible organizations only
      const userOrgIds = this.organizations.map(o => o.ID);
      const userChannels = this.channels
        .filter(c => userOrgIds.includes(c.OrganizationID))
        .map(c => `'${c.ID}'`)
        .join(',');

      if (userChannels) {
        filters.push(`ChannelID IN (${userChannels})`);
      }
    }

    return filters.length > 0 ? filters.join(' AND ') : null;
  }

  /**
   * Calculate the start date for data loading, including previous period for comparisons.
   * Returns a date that includes both current and previous period.
   */
  private buildDateFilter(): Date | null {
    const now = new Date();

    switch (this.selectedTimeRange) {
      case 'today':
        // Load today + yesterday (for comparison)
        return new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000);
      case '7d':
        // Load last 14 days (7 days + 7 days previous for comparison)
        return new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
      case '30d':
        // Load last 60 days (30 days + 30 days previous for comparison)
        return new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
      case '90d':
        // Load last 180 days (90 days + 90 days previous for comparison)
        return new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
      case 'all':
        // No filter - load all data
        return null;
    }
  }

  async onTimeRangeChange(range: TimeRange): Promise<void> {
    this.selectedTimeRange = range;
    if (range !== 'custom') {
      this.customStartDate = null;
      this.customEndDate = null;
      this.showDatePicker = false;
    }
    this.saveUserSettings();
    this.updateQueryParams();
    await this.refresh();
  }

  protected toggleDatePicker(): void {
    this.showDatePicker = !this.showDatePicker;
  }

  protected closeDatePicker(): void {
    this.showDatePicker = false;
  }

  protected async onCustomDateChange(): Promise<void> {
    if (this.customStartDate && this.customEndDate) {
      this.selectedTimeRange = 'custom';
      this.showDatePicker = false;
      this.saveUserSettings();
      this.updateQueryParams();
      await this.refresh();
    }
  }

  protected get dateRangeLabel(): string {
    if (this.selectedTimeRange === 'custom' && this.customStartDate && this.customEndDate) {
      const start = new Date(this.customStartDate + 'T00:00:00');
      const end = new Date(this.customEndDate + 'T00:00:00');
      const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
      const startStr = start.toLocaleDateString('en-US', opts);
      const endStr = end.toLocaleDateString('en-US', { ...opts, year: 'numeric' });
      return `${startStr} – ${endStr}`;
    }
    const match = this.timeRanges.find(r => r.value === this.selectedTimeRange);
    return match?.label || 'Select dates';
  }

  async onOrganizationChange(orgId: string | null): Promise<void> {
    this.selectedOrganizationId = orgId;
    this.selectedOrg = orgId ? this.organizations.find(o => o.ID === orgId) || null : null;
    this.loadSLASettings(); // Reload SLA settings for new org
    await this.loadAdminContext(); // Reload admin context for new org
    this.saveUserSettings();
    this.updateQueryParams();

    // Reload data with new organization filter
    await this.refresh();
  }

  // ========== Org Selection Methods ==========

  /**
   * Applies an org change (deep link or the global header switcher via
   * IzzyContextService). Receives mjBizAppsCommonOrganizationEntity | null;
   * null legacy-renders the all-orgs aggregate but is no longer selectable.
   *
   * `syncUrl` is false only for context-originated changes (another section's
   * org switch while this tab is hidden) — location.replaceState is global, so
   * a hidden tab must not rewrite the visible tab's URL.
   */
  async onOrgSelectorChange(org: mjBizAppsCommonOrganizationEntity | null, syncUrl: boolean = true): Promise<void> {
    this.selectedOrganizationId = org?.ID || null;
    this.selectedOrg = org;
    this.contextService.selectedOrganizationId = org?.ID || null;
    this.loadSLASettings();
    await this.loadAdminContext();
    this.saveUserSettings();
    if (syncUrl) {
      this.updateQueryParams();
    }

    // Reload data with new organization filter
    await this.refresh();
  }

  getOrgInitials(name: string): string {
    if (!name) return '?';
    const words = name.split(' ').filter(w => w.length > 0);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  /**
   * Load SLA settings from org settings.
   * Uses GetOrganizationSettingValue for org-level defaults.
   */
  private loadSLASettings(): void {
    if (!this.selectedOrganizationId) {
      // Use system defaults if no org selected
      this.slaSettings = {
        enabled: true,
        targetMinutes: 10,
        warningThresholdPercent: 80,
        businessHoursOnly: false,
        businessHoursStart: '09:00',
        businessHoursEnd: '17:00'
      };
      return;
    }

    const engine = IzzyMetadataEngine.Instance;

    // Get SLA settings for the selected organization
    const enabledResult = engine.GetOrganizationSettingValue('SLAEnabled', this.selectedOrganizationId);
    const targetResult = engine.GetOrganizationSettingValue('SLATargetResponseMinutes', this.selectedOrganizationId);
    const warningResult = engine.GetOrganizationSettingValue('SLAWarningThresholdPercent', this.selectedOrganizationId);
    const businessHoursOnlyResult = engine.GetOrganizationSettingValue('SLABusinessHoursOnly', this.selectedOrganizationId);
    const businessHoursStartResult = engine.GetOrganizationSettingValue('SLABusinessHoursStart', this.selectedOrganizationId);
    const businessHoursEndResult = engine.GetOrganizationSettingValue('SLABusinessHoursEnd', this.selectedOrganizationId);

    this.slaSettings = {
      enabled: enabledResult.value?.toLowerCase() === 'true',
      targetMinutes: targetResult.value ? parseFloat(targetResult.value) : 60,
      warningThresholdPercent: warningResult.value ? parseFloat(warningResult.value) : 80,
      businessHoursOnly: businessHoursOnlyResult.value?.toLowerCase() === 'true',
      businessHoursStart: businessHoursStartResult.value || '09:00',
      businessHoursEnd: businessHoursEndResult.value || '17:00'
    };
  }

  /**
   * Get SLA settings for a specific channel (supports channel-level override)
   */
  getSLASettingsForChannel(channelId: string): SLASettings {
    const engine = IzzyMetadataEngine.Instance;

    return {
      enabled: engine.GetSettingValueBoolean('SLAEnabled', channelId, true),
      targetMinutes: engine.GetSettingValueNumber('SLATargetResponseMinutes', channelId, 10),
      warningThresholdPercent: engine.GetSettingValueNumber('SLAWarningThresholdPercent', channelId, 80),
      businessHoursOnly: engine.GetSettingValueBoolean('SLABusinessHoursOnly', channelId, false),
      businessHoursStart: engine.GetSettingValueSimple('SLABusinessHoursStart', channelId) || '09:00',
      businessHoursEnd: engine.GetSettingValueSimple('SLABusinessHoursEnd', channelId) || '17:00'
    };
  }

  // ========== Admin Context & Assignment Analytics ==========

  /**
   * Detect if the current user is an admin/owner and load team members.
   * Uses same pattern as message-approval component.
   */
  private async loadAdminContext(): Promise<void> {
    const currentContactId = IzzyMetadataEngine.Instance.CurrentContactID;
    const orgContacts = IzzyMetadataEngine.Instance.OrganizationContacts as BCSaaSOrganizationPersonRoleEntity[];

    // Determine the org to check against. Admin status is decided by membership
    // Level (>= ADMIN) via BCMembershipService — same pattern as message-approval.
    // The post-BAC OrganizationPersonRole row has no denormalized role-name column,
    // so the old ['Owner','Admin'].includes(oc.Role) check always evaluated false
    // (oc.Role is blank) and the Assignments section never appeared. Level is the
    // canonical signal.
    const orgId = this.selectedOrganizationId;
    const engine = IzzyMetadataEngine.Instance;
    if (!currentContactId) {
      this.isCurrentUserAdmin = false;
    } else if (engine.CurrentUserTenantMemberships) {
      // Preferred: the middleware-stamped tenant context via the engine.
      // HasMembershipLevel from the browser is tenant-filtered since BCSaaS v1.5
      // (SkipTenantFilter doesn't survive the GraphQL transport), so it returns
      // false for any org other than the current context org.
      this.isCurrentUserAdmin = orgId
        ? engine.CurrentUserHasMembershipLevel(BC_ROLE_LEVELS.ADMIN, orgId)
        : engine.CurrentUserHasMembershipLevel(BC_ROLE_LEVELS.ADMIN);
    } else if (!orgId) {
      // If "All Organizations", check if admin in ANY of the user's active orgs
      const myOrgIds = [...new Set(
        orgContacts
          .filter(oc => oc.PersonID === currentContactId && IzzyMetadataEngine.Instance.IsActiveMembership(oc))
          .map(oc => oc.OrganizationID)
      )];
      const adminChecks = await Promise.all(
        myOrgIds.map(id => BCMembershipService.HasMembershipLevel(
          currentContactId, id, BC_ROLE_LEVELS.ADMIN, Metadata.Provider.CurrentUser
        ))
      );
      this.isCurrentUserAdmin = adminChecks.some(Boolean);
    } else {
      this.isCurrentUserAdmin = await BCMembershipService.HasMembershipLevel(
        currentContactId, orgId, BC_ROLE_LEVELS.ADMIN, Metadata.Provider.CurrentUser
      );
    }

    // Conditionally add/remove assignments section
    const hasAssignmentsSection = this.sections.some(s => s.id === 'assignments');
    if (this.isCurrentUserAdmin && !hasAssignmentsSection) {
      this.sections.push({
        id: 'assignments',
        label: 'Assignments',
        icon: 'fa-user-check',
        description: 'Team workload and message assignment tracking'
      });
    } else if (!this.isCurrentUserAdmin && hasAssignmentsSection) {
      this.sections = this.sections.filter(s => s.id !== 'assignments');
      if (this.selectedSection.id === 'assignments') {
        this.selectedSection = this.sections[0];
      }
    }

    // Load team members for assignment display. Needed by BOTH the admin-only message
    // Assignments section AND the everyone-visible ticket "Tickets by Assignee" workload,
    // so resolve names regardless of admin status (member names aren't admin-sensitive —
    // they already appear across the ticket UI).
    await this.loadTeamMembers(orgContacts);
  }

  /**
   * Load team member names from Contact records for display.
   */
  private async loadTeamMembers(orgContacts: BCSaaSOrganizationPersonRoleEntity[]): Promise<void> {
    const orgId = this.selectedOrganizationId;
    const activeContacts = orgId
      ? orgContacts.filter(oc => oc.OrganizationID === orgId && IzzyMetadataEngine.Instance.IsActiveMembership(oc))
      : orgContacts.filter(oc => {
          const userOrgIds = this.organizations.map(o => o.ID);
          return userOrgIds.includes(oc.OrganizationID) && IzzyMetadataEngine.Instance.IsActiveMembership(oc);
        });

    const contactIds = [...new Set(activeContacts.map(oc => oc.PersonID))];
    if (contactIds.length === 0) {
      this.orgTeamMembers = [];
      return;
    }

    const rv = new RunView();
    const idList = contactIds.map(id => `'${id}'`).join(',');
    const result = await rv.RunView<mjBizAppsCommonPersonEntity>({
      EntityName: 'MJ_BizApps_Common: People',
      ExtraFilter: `ID IN (${idList})`,
      ResultType: 'entity_object'
    });

    if (result?.Success) {
      this.orgTeamMembers = result.Results.map(c => ({
        contactId: c.ID,
        name: `${c.FirstName} ${c.LastName}`.trim() || c.Email || 'Unknown'
      }));
    } else {
      this.orgTeamMembers = [];
    }
  }

  /**
   * Compute category breakdown from filtered messages.
   */
  private computeCategoryBreakdown(messages: IzzyChannelMessageEntity[]): void {
    // Brand dataviz series (validated 4-slot palette; tiles carry their own
    // labels, so repeats past 4 read fine — the label is the identity).
    const categoryColors = [
      'var(--izzy-chart-1)', 'var(--izzy-chart-2)',
      'var(--izzy-chart-3)', 'var(--izzy-chart-4)'
    ];

    const categoryCounts = new Map<string, number>();
    let uncategorized = 0;

    messages.forEach(m => {
      const category = m.MessageCategory;
      if (category) {
        categoryCounts.set(category, (categoryCounts.get(category) || 0) + 1);
      } else {
        uncategorized++;
      }
    });

    this.totalCategorized = messages.length - uncategorized;
    this.totalUncategorized = uncategorized;

    const total = this.totalCategorized || 1;
    const maxCount = Math.max(...Array.from(categoryCounts.values()), 1);
    const minFontSize = 16;
    const maxFontSize = 64;
    let colorIndex = 0;
    this.categoryBreakdown = Array.from(categoryCounts.entries())
      .map(([category, count]) => {
        const pct = (count / total) * 100;
        const rounded = Math.round(pct);
        const scale = count / maxCount;
        return {
          category,
          count,
          percentage: rounded,
          displayPercentage: count > 0 && rounded === 0 ? '<1%' : `${rounded}%`,
          color: categoryColors[colorIndex++ % categoryColors.length],
          cloudFontSize: Math.round(minFontSize + scale * (maxFontSize - minFontSize))
        };
      })
      .sort((a, b) => b.count - a.count);
  }

  // ========== Tickets analytics (§13) ==========

  /**
   * Issues scoped to the active org + time range. Mirrors filterMessagesByTimeAndOrg, but an Issue
   * has no org/channel of its own — it's in scope when one of the (already org-filtered) messages in
   * `filteredMessages` links to it. Time window is the issue's creation date.
   */
  private filterTicketsByTimeAndOrg(filteredMessages: IzzyChannelMessageEntity[]): mjBizAppsIssuesIssueEntity[] {
    const orgIssueIds = new Set(filteredMessages.map(m => m.IssueID).filter((id): id is string => !!id));
    if (orgIssueIds.size === 0) return [];
    const cutoffDate = this.getCutoffDate();
    const endDate = this.getEndDate();
    return this.allTickets.filter(t => {
      if (!orgIssueIds.has(t.ID)) return false;
      const created = t.__mj_CreatedAt ? new Date(t.__mj_CreatedAt) : null;
      if (created && cutoffDate && created < cutoffDate) return false;
      if (created && endDate && created > endDate) return false;
      return true;
    });
  }

  /**
   * Derive the Tickets-section KPIs + breakdowns from the loaded tickets/SLA data, scoped to the
   * current org + period. Mirrors the message compute* methods. CSAT / AI-resolution metrics are
   * intentionally absent (SatisfactionSurvey is on hold; AIResolved was deliberately not added) — the
   * UI shows a "coming with CSAT" note for those.
   */
  private computeTicketAnalytics(filteredMessages: IzzyChannelMessageEntity[]): void {
    const tickets = this.filterTicketsByTimeAndOrg(filteredMessages);
    const slaByIssue = new Map(this.allSlaStates.map(s => [s.IssueID, s]));
    const statusName = (id: string | null) => (id ? this.issueStatusById.get(id)?.name ?? 'Unknown' : 'Unknown');
    const isResolvedName = (n: string) => n === 'Resolved';
    const isClosedOut = (id: string | null) => {
      if (!id) return false;
      const s = this.issueStatusById.get(id);
      return !!s && (s.isTerminal || s.name === 'Resolved');
    };

    // KPIs
    const created = tickets.length;
    const resolvedTickets = tickets.filter(t => isClosedOut(t.StatusID));
    const resolved = resolvedTickets.length;
    const backlog = tickets.filter(t => !isClosedOut(t.StatusID)).length;

    // Avg resolution time (ResolvedAt − CreatedAt), hours.
    const resolutionHours = resolvedTickets
      .map(t => {
        const c = t.__mj_CreatedAt ? new Date(t.__mj_CreatedAt).getTime() : null;
        const r = t.ResolvedAt ? new Date(t.ResolvedAt).getTime() : null;
        return c && r && r >= c ? (r - c) / 3_600_000 : null;
      })
      .filter((h): h is number => h !== null);
    this.ticketKpis.avgResolutionHours = resolutionHours.length
      ? Math.round((resolutionHours.reduce((a, b) => a + b, 0) / resolutionHours.length) * 10) / 10 : null;

    // Avg first response (FirstResponseAt − CreatedAt), hours — from SLA state.
    const frHours = tickets
      .map(t => {
        const sla = slaByIssue.get(t.ID);
        const c = t.__mj_CreatedAt ? new Date(t.__mj_CreatedAt).getTime() : null;
        const fr = sla?.FirstResponseAt ? new Date(sla.FirstResponseAt).getTime() : null;
        return c && fr && fr >= c ? (fr - c) / 3_600_000 : null;
      })
      .filter((h): h is number => h !== null);
    this.ticketKpis.avgFirstResponseHours = frHours.length
      ? Math.round((frHours.reduce((a, b) => a + b, 0) / frHours.length) * 10) / 10 : null;

    // SLA split (met / near-breach / breached) over tickets that have an SLA clock.
    let met = 0, nearBreach = 0, breached = 0;
    for (const t of tickets) {
      const sla = slaByIssue.get(t.ID);
      if (!sla) continue;
      if (sla.Breached) breached++;
      else if (sla.WarningFiredAt) nearBreach++;
      else met++;
    }
    this.slaSplit = { met, nearBreach, breached };
    const slaTotal = met + nearBreach + breached;
    this.ticketKpis.slaCompliancePct = slaTotal > 0 ? Math.round((met / slaTotal) * 100) : null;

    this.ticketKpis.created = created;
    this.ticketKpis.resolved = resolved;
    this.ticketKpis.backlog = backlog;

    // Reopen rate: tickets reopened after Resolved — inferred from a non-closed-out status on a
    // ticket that has a ResolvedAt stamp (it was resolved once, then moved back).
    const everResolved = tickets.filter(t => t.ResolvedAt).length;
    const reopened = tickets.filter(t => t.ResolvedAt && !isClosedOut(t.StatusID)).length;
    this.reopenRatePct = everResolved > 0 ? Math.round((reopened / everResolved) * 100) : null;

    // Breakdowns (category → count) for the bar charts.
    this.ticketsByStatus = this.countBy(tickets, t => statusName(t.StatusID));
    this.ticketsByType = this.countBy(tickets, t => (t.IssueTypeID ? this.issueTypeNameById.get(t.IssueTypeID) ?? 'Unknown' : 'Unknown'));

    // Per-assignee ticket workload — total tickets per assignee split into open vs resolved
    // (mirrors the message Team Workload's pending/escalated split). Includes an Unassigned row.
    const byAssignee = new Map<string | null, mjBizAppsIssuesIssueEntity[]>();
    for (const t of tickets) {
      const key = t.AssigneeRecordID || null;
      const group = byAssignee.get(key) ?? [];
      group.push(t);
      byAssignee.set(key, group);
    }
    const summaries: TicketAssignmentSummary[] = [];
    for (const [personId, group] of byAssignee) {
      const resolvedCount = group.filter(t => isClosedOut(t.StatusID)).length;
      summaries.push({
        personId,
        personName: personId ? this.agentDisplayName(personId) : 'Unassigned',
        count: group.length,
        percentage: created > 0 ? Math.round((group.length / created) * 100) : 0,
        openCount: group.length - resolvedCount,
        resolvedCount
      });
    }
    // Assigned members desc by count; Unassigned always last (matches message workload ordering).
    summaries.sort((a, b) => {
      if (a.personId === null) return 1;
      if (b.personId === null) return -1;
      return b.count - a.count;
    });
    this.ticketAssignmentSummaries = summaries;
    this.totalTickets = created;
    this.totalTicketsAssigned = tickets.filter(t => t.AssigneeRecordID).length;
    this.totalTicketsUnassigned = created - this.totalTicketsAssigned;
    this.maxTicketAssignmentCount = summaries.length ? Math.max(...summaries.map(s => s.count)) : 0;

    // By channel: each ticket → the channel of its linked messages (use the filtered set).
    const channelByIssue = new Map<string, string>();
    for (const m of filteredMessages) {
      if (m.IssueID && !channelByIssue.has(m.IssueID)) {
        const ch = this.channels.find(c => c.ID === m.ChannelID);
        channelByIssue.set(m.IssueID, ch?.Name || 'Unknown');
      }
    }
    this.ticketsByChannel = this.countBy(tickets, t => channelByIssue.get(t.ID) || 'Unknown');

    this.ticketsLoaded = true;
  }

  /** Group items by a key fn into sorted {label, value} rows (desc by count). */
  private countBy<T>(items: T[], keyFn: (item: T) => string): TicketBreakdown[] {
    const counts = new Map<string, number>();
    for (const item of items) {
      const k = keyFn(item);
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    return Array.from(counts, ([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);
  }

  /** Best-effort agent display name from the loaded org team members (falls back to short ID). */
  private agentDisplayName(personId: string): string {
    return this.orgTeamMembers.find(m => m.contactId === personId)?.name || personId.slice(0, 8);
  }

  protected onCategoryClick(item: CategoryBreakdown): void {
    const filteredMessages = this.filterMessagesByTimeAndOrg(this.allMessages);
    this.openDrillDown({
      type: 'category',
      title: `Category: ${item.category}`,
      value: `${item.count} messages (${item.displayPercentage})`,
      icon: 'fa-tag',
      color: item.color,
      filterFn: (m) => m.MessageCategory === item.category
    }, filteredMessages);
  }

  /**
   * Compute assignment analytics from filtered messages.
   * Only considers Pending and Escalated approval statuses.
   */
  private computeAssignmentAnalytics(messages: IzzyChannelMessageEntity[]): void {
    const activeMessages = messages.filter(
      m => m.ApprovalStatus === 'Pending' || m.ApprovalStatus === 'Escalated'
    );
    const approvedMessages = messages.filter(m => m.ApprovalStatus === 'Approved');

    // "Active" KPIs keep their original meaning: pending + escalated only.
    this.totalActiveMessages = activeMessages.length;
    this.totalAssigned = activeMessages.filter(m => m.AssignedToPersonID).length;
    this.totalUnassigned = activeMessages.filter(m => !m.AssignedToPersonID).length;

    // OPEN mode — current workload, the card's original meaning: who owns the
    // pending/escalated queue right now. Unassigned last.
    const openGrouped = new Map<string | null, IzzyChannelMessageEntity[]>();
    for (const msg of activeMessages) {
      const key = msg.AssignedToPersonID || null;
      const group = openGrouped.get(key) || [];
      group.push(msg);
      openGrouped.set(key, group);
    }
    const openSummaries: AssignmentSummary[] = [];
    for (const [contactId, msgs] of openGrouped) {
      openSummaries.push({
        contactId,
        contactName: contactId
          ? (this.orgTeamMembers.find(m => m.contactId === contactId)?.name || 'Unknown')
          : 'Unassigned',
        count: msgs.length,
        percentage: activeMessages.length > 0
          ? Math.round((msgs.length / activeMessages.length) * 100)
          : 0,
        pendingCount: msgs.filter(m => m.ApprovalStatus === 'Pending').length,
        escalatedCount: msgs.filter(m => m.ApprovalStatus === 'Escalated').length,
        approvedCount: 0
      });
    }
    openSummaries.sort((a, b) => {
      if (a.contactId === null) return 1;
      if (b.contactId === null) return -1;
      return b.count - a.count;
    });

    // HANDLED mode — approvals in the period, attributed by the AIAutoApproved FLAG
    // (not by null-assignee, which false-positives on legacy human approvals from
    // before auto-assignment existed): AIAutoApproved → the synthetic "Izzy" row;
    // otherwise the assignee (human approvals auto-assign the approver as of
    // 2026-08-24); human approvals with no assignee are pre-feature history and
    // land in an "Unattributed" row that shrinks to zero going forward. Izzy
    // competes in count order like a team member — this view is throughput.
    const handledGrouped = new Map<string, IzzyChannelMessageEntity[]>();
    const IZZY_KEY = '__izzy__';
    const UNATTRIBUTED_KEY = '__unattributed__';
    for (const msg of approvedMessages) {
      const key = msg.AIAutoApproved ? IZZY_KEY : (msg.AssignedToPersonID || UNATTRIBUTED_KEY);
      const group = handledGrouped.get(key) || [];
      group.push(msg);
      handledGrouped.set(key, group);
    }
    const handledSummaries: AssignmentSummary[] = [];
    for (const [key, msgs] of handledGrouped) {
      const isIzzy = key === IZZY_KEY;
      const isUnattributed = key === UNATTRIBUTED_KEY;
      handledSummaries.push({
        contactId: (isIzzy || isUnattributed) ? null : key,
        contactName: isIzzy
          ? 'Izzy'
          : isUnattributed
            ? 'Unattributed'
            : (this.orgTeamMembers.find(m => m.contactId === key)?.name || 'Unknown'),
        count: msgs.length,
        percentage: approvedMessages.length > 0
          ? Math.round((msgs.length / approvedMessages.length) * 100)
          : 0,
        pendingCount: 0,
        escalatedCount: 0,
        approvedCount: msgs.length,
        isIzzy
      });
    }
    // Members + Izzy by throughput; Unattributed (legacy) always last.
    handledSummaries.sort((a, b) => {
      const aLegacy = !a.contactId && !a.isIzzy ? 1 : 0;
      const bLegacy = !b.contactId && !b.isIzzy ? 1 : 0;
      if (aLegacy !== bLegacy) return aLegacy - bLegacy;
      return b.count - a.count;
    });

    this.openWorkloadSummaries = openSummaries;
    this.handledWorkloadSummaries = handledSummaries;
    this.maxOpenWorkloadCount = openSummaries.length > 0 ? Math.max(...openSummaries.map(s => s.count)) : 0;
    this.maxHandledWorkloadCount = handledSummaries.length > 0 ? Math.max(...handledSummaries.map(s => s.count)) : 0;
  }

  private computeAnalytics(): void {
    const filteredMessages = this.filterMessagesByTimeAndOrg(this.allMessages);
    const filteredRuns = this.filterRunsByTimeAndOrg(this.allRuns);

    // Get previous period messages for comparison
    this.previousPeriodMessages = this.getPreviousPeriodMessages();

    this.computeKPIs(filteredMessages, filteredRuns);
    this.computeMessageVolumeTrend(filteredMessages);
    this.computeGenerationStatusBreakdown(filteredMessages);
    this.computeApprovalStatusBreakdown(filteredMessages);
    this.computeChannelPerformance(filteredMessages);
    this.computeTopSenders(filteredMessages);
    this.computeHeatmapData(filteredMessages);
    this.computeSLAMetrics(filteredMessages);
    this.computeFeedbackAnalytics(filteredMessages);
    this.computeCategoryBreakdown(filteredMessages);
    this.computeTicketAnalytics(filteredMessages);
    if (this.isCurrentUserAdmin) {
      this.computeAssignmentAnalytics(filteredMessages);
    }
    this.recentMessages = filteredMessages.slice(0, 10);
    this.recentRuns = filteredRuns.slice(0, 5);

    // Re-render charts after data update
    setTimeout(() => {
      if (this.selectedSection.id === 'overview') {
        this.renderLineChart();
      } else if (this.selectedSection.id === 'activity') {
        this.renderHeatmap();
      } else if (this.selectedSection.id === 'performance') {
        this.renderChannelComparisonChart();
      } else if (this.selectedSection.id === 'tickets') {
        this.renderTicketCharts();
      }
    }, 0);
  }

  private filterMessagesByTimeAndOrg(messages: IzzyChannelMessageEntity[]): IzzyChannelMessageEntity[] {
    const cutoffDate = this.getCutoffDate();
    const endDate = this.getEndDate();

    return messages.filter(m => {
      const received = new Date(m.ReceivedAt);
      if (cutoffDate && received < cutoffDate) return false;
      if (endDate && received > endDate) return false;

      if (this.selectedOrganizationId) {
        const channel = this.channels.find(c => c.ID === m.ChannelID);
        if (!channel || channel.OrganizationID !== this.selectedOrganizationId) {
          return false;
        }
      }

      return true;
    });
  }

  private filterRunsByTimeAndOrg(runs: BCSaaSChannelRunEntity[]): BCSaaSChannelRunEntity[] {
    const cutoffDate = this.getCutoffDate();
    const endDate = this.getEndDate();

    return runs.filter(r => {
      const started = new Date(r.StartedAt);
      if (cutoffDate && started < cutoffDate) return false;
      if (endDate && started > endDate) return false;

      if (this.selectedOrganizationId) {
        const channel = this.channels.find(c => c.ID === r.ChannelID);
        if (!channel || channel.OrganizationID !== this.selectedOrganizationId) {
          return false;
        }
      }

      return true;
    });
  }

  private getCutoffDate(): Date | null {
    if (this.selectedTimeRange === 'custom' && this.customStartDate) {
      return new Date(this.customStartDate + 'T00:00:00');
    }
    const now = new Date();
    switch (this.selectedTimeRange) {
      case 'today':
        return new Date(now.getFullYear(), now.getMonth(), now.getDate());
      case '7d':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      case '30d':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      case '90d':
        return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      case 'all':
        return null;
      default:
        return null;
    }
  }

  private getEndDate(): Date | null {
    if (this.selectedTimeRange === 'custom' && this.customEndDate) {
      const end = new Date(this.customEndDate + 'T00:00:00');
      end.setHours(23, 59, 59, 999);
      return end;
    }
    return null;
  }

  /**
   * Get messages from the previous equivalent time period for comparison
   */
  private getPreviousPeriodMessages(): IzzyChannelMessageEntity[] {
    const now = new Date();
    let currentStart: Date;
    let previousStart: Date;
    let previousEnd: Date;

    switch (this.selectedTimeRange) {
      case 'today':
        currentStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        previousStart = new Date(currentStart.getTime() - 24 * 60 * 60 * 1000);
        previousEnd = currentStart;
        break;
      case '7d':
        currentStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        previousStart = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
        previousEnd = currentStart;
        break;
      case '30d':
        currentStart = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        previousStart = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
        previousEnd = currentStart;
        break;
      case '90d':
        currentStart = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        previousStart = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
        previousEnd = currentStart;
        break;
      case 'all':
        return [];
    }

    return this.allMessages.filter(m => {
      const receivedAt = new Date(m.ReceivedAt);
      if (receivedAt < previousStart || receivedAt >= previousEnd) {
        return false;
      }

      if (this.selectedOrganizationId) {
        const channel = this.channels.find(c => c.ID === m.ChannelID);
        if (!channel || channel.OrganizationID !== this.selectedOrganizationId) {
          return false;
        }
      }

      return true;
    });
  }

  private computeKPIs(messages: IzzyChannelMessageEntity[], runs: BCSaaSChannelRunEntity[]): void {
    const totalMessages = messages.length;
    const generatedCount = messages.filter(m => m.GenerationStatus === 'Generated').length;
    const pendingApproval = messages.filter(m => m.ApprovalStatus === 'Pending').length;
    const approvedCount = messages.filter(m => m.ApprovalStatus === 'Approved').length;

    const successRate = totalMessages > 0 ? Math.round((generatedCount / totalMessages) * 100) : 0;
    const approvalRate = generatedCount > 0 ? Math.round((approvedCount / generatedCount) * 100) : 0;

    // Calculate runs stats
    const completedRuns = runs.filter(r => r.Status === 'Completed').length;
    const totalRuns = runs.length;
    const runSuccessRate = totalRuns > 0 ? Math.round((completedRuns / totalRuns) * 100) : 0;

    // Calculate response time stats
    const responseTimes = this.calculateResponseTimes(messages);
    this.avgResponseTimeMinutes = responseTimes.avg;
    this.minResponseTimeMinutes = responseTimes.min;
    this.maxResponseTimeMinutes = responseTimes.max;

    // Calculate previous period metrics for comparison
    const prevTotal = this.previousPeriodMessages.length;
    const prevGenerated = this.previousPeriodMessages.filter(m => m.GenerationStatus === 'Generated').length;
    const prevApproved = this.previousPeriodMessages.filter(m => m.ApprovalStatus === 'Approved').length;
    const prevPending = this.previousPeriodMessages.filter(m => m.ApprovalStatus === 'Pending').length;
    const prevResponseTimes = this.calculateResponseTimes(this.previousPeriodMessages);

    this.kpiCards = [
      {
        title: 'Total Messages',
        value: this.formatNumber(totalMessages),
        rawValue: totalMessages,
        previousValue: prevTotal,
        icon: 'fa-envelope',
        color: 'var(--izzy-chart-1)',
        ...this.calculateTrend(totalMessages, prevTotal)
      },
      {
        title: 'Replies Generated',
        value: this.formatNumber(generatedCount),
        rawValue: generatedCount,
        previousValue: prevGenerated,
        change: successRate,
        changeLabel: 'success rate',
        changeIsPercent: true,
        icon: 'fa-robot',
        color: 'var(--izzy-ai)',
        ...this.calculateTrend(generatedCount, prevGenerated)
      },
      {
        title: 'Response Time',
        value: this.formatDuration(responseTimes.median),
        rawValue: responseTimes.median,
        previousValue: prevResponseTimes.median,
        change: responseTimes.count > 0 ? responseTimes.count : undefined,
        changeLabel: responseTimes.count === 1 ? 'reply measured' : 'replies measured',
        icon: 'fa-stopwatch',
        color: 'var(--izzy-chart-2)',
        // For response time, lower is better (swap args to invert arrow direction)
        ...this.calculateTrend(prevResponseTimes.median, responseTimes.median)
      },
      {
        title: 'Pending Approval',
        value: this.formatNumber(pendingApproval),
        rawValue: pendingApproval,
        previousValue: prevPending,
        change: totalMessages > 0 ? Math.round((pendingApproval / totalMessages) * 100) : undefined,
        changeLabel: 'of total messages',
        changeIsPercent: true,
        icon: 'fa-clock',
        color: 'var(--izzy-warning)',
        // For pending, lower is better
        ...this.calculateTrend(prevPending, pendingApproval)
      },
      {
        title: 'Approved',
        value: this.formatNumber(approvedCount),
        rawValue: approvedCount,
        previousValue: prevApproved,
        change: approvalRate,
        changeLabel: 'approval rate',
        changeIsPercent: true,
        icon: 'fa-check-circle',
        color: 'var(--izzy-success)',
        ...this.calculateTrend(approvedCount, prevApproved)
      },
      {
        title: 'BC: Channel Runs',
        value: this.formatNumber(totalRuns),
        rawValue: totalRuns,
        change: runSuccessRate,
        changeLabel: 'success rate',
        changeIsPercent: true,
        icon: 'fa-sync',
        color: 'var(--izzy-chart-3)'
      }
    ];
  }

  /**
   * Calculate trend direction and percentage
   */
  private calculateTrend(current: number, previous: number): { trend?: 'up' | 'down' | 'flat'; trendPercent?: number } {
    if (this.selectedTimeRange === 'all' || previous === 0 || previous === undefined) {
      return {};
    }

    const percentChange = Math.round(((current - previous) / previous) * 100);

    if (percentChange === 0) {
      return { trend: 'flat', trendPercent: 0 };
    }

    return {
      trend: percentChange > 0 ? 'up' : 'down',
      trendPercent: Math.abs(percentChange)
    };
  }

  private calculateResponseTimes(messages: IzzyChannelMessageEntity[]): { avg: number; median: number; min: number; max: number; count: number } {
    const times: number[] = [];

    for (const msg of messages) {
      if (msg.GenerationStatus !== 'Generated') continue;

      const receivedAt = new Date(msg.ReceivedAt);
      const generatedAt = msg.GeneratedAt
        ? new Date(msg.GeneratedAt)
        : (msg.__mj_UpdatedAt ? new Date(msg.__mj_UpdatedAt) : null);

      if (generatedAt && receivedAt) {
        const diffMs = generatedAt.getTime() - receivedAt.getTime();
        if (diffMs >= 0) {
          times.push(diffMs / 60000);
        }
      }
    }

    if (times.length === 0) {
      return { avg: 0, median: 0, min: 0, max: 0, count: 0 };
    }

    const sorted = [...times].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const median = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];

    const sum = times.reduce((a, b) => a + b, 0);
    return {
      avg: sum / times.length,
      median,
      min: Math.min(...times),
      max: Math.max(...times),
      count: times.length
    };
  }

  /**
   * Compute SLA metrics using org-level SLA settings
   */
  private computeSLAMetrics(messages: IzzyChannelMessageEntity[]): void {
    if (!this.slaSettings.enabled) {
      this.slaMetCount = 0;
      this.slaMissedCount = 0;
      this.slaMetPercent = 0;
      return;
    }

    let metCount = 0;
    let missedCount = 0;

    for (const msg of messages) {
      if (msg.GenerationStatus !== 'Generated') continue;

      const receivedAt = new Date(msg.ReceivedAt);
      const generatedAt = msg.GeneratedAt
        ? new Date(msg.GeneratedAt)
        : (msg.__mj_UpdatedAt ? new Date(msg.__mj_UpdatedAt) : null);

      if (generatedAt && receivedAt) {
        const diffMinutes = (generatedAt.getTime() - receivedAt.getTime()) / 60000;
        if (diffMinutes <= this.slaSettings.targetMinutes) {
          metCount++;
        } else {
          missedCount++;
        }
      }
    }

    this.slaMetCount = metCount;
    this.slaMissedCount = missedCount;
    this.slaMetPercent = (metCount + missedCount) > 0
      ? Math.round((metCount / (metCount + missedCount)) * 100)
      : 0;
  }

  formatDuration(minutes: number): string {
    if (minutes === 0) return 'N/A';
    if (minutes < 1) return `${Math.round(minutes * 60)}s`;
    if (minutes < 60) return `${Math.round(minutes)}m`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }

  private computeMessageVolumeTrend(messages: IzzyChannelMessageEntity[]): void {
    const days = this.getDaysForRange();
    const trend: ChartDataPoint[] = [];

    // For custom ranges, anchor to the custom end date; otherwise use today
    const endAnchor = (this.selectedTimeRange === 'custom' && this.customEndDate)
      ? new Date(this.customEndDate + 'T00:00:00')
      : new Date();

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(endAnchor);
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);

      const nextDate = new Date(date);
      nextDate.setDate(nextDate.getDate() + 1);

      const dayMessages = messages.filter(m => {
        const receivedAt = new Date(m.ReceivedAt);
        return receivedAt >= date && receivedAt < nextDate;
      });

      const totalCount = dayMessages.length;
      const approvedCount = dayMessages.filter(m => m.ApprovalStatus === 'Approved').length;
      const dayResponseTimes = this.calculateResponseTimes(dayMessages);

      trend.push({
        label: this.formatDateLabel(date),
        date: date,
        value: totalCount,
        approved: approvedCount,
        avgResponseTime: dayResponseTimes.avg
      });
    }

    this.messageVolumeTrend = trend;
    this.maxMessageVolume = Math.max(...trend.map(t => t.value), 1);
  }

  private computeFeedbackAnalytics(messages: IzzyChannelMessageEntity[]): void {
    const ratedMessages = messages.filter(m => m.QualityFeedback !== null);

    this.feedbackTotalUp = ratedMessages.filter(m => m.QualityFeedback === 'Up').length;
    this.feedbackTotalDown = ratedMessages.filter(m => m.QualityFeedback === 'Down').length;
    this.feedbackTotalRated = ratedMessages.length;
    this.feedbackThumbsUpPercent = this.feedbackTotalRated > 0
      ? Math.round((this.feedbackTotalUp / this.feedbackTotalRated) * 100)
      : 0;

    // Breakdown by channel
    const byChannel = new Map<string, { up: number; down: number }>();
    for (const m of ratedMessages) {
      const existing = byChannel.get(m.ChannelID) || { up: 0, down: 0 };
      if (m.QualityFeedback === 'Up') existing.up++;
      else existing.down++;
      byChannel.set(m.ChannelID, existing);
    }

    this.feedbackByChannel = Array.from(byChannel.entries()).map(([channelId, stats]) => {
      const channel = this.channels.find(c => c.ID === channelId);
      const total = stats.up + stats.down;
      return {
        channelId,
        channelName: channel?.Name || 'Unknown Channel',
        thumbsUp: stats.up,
        thumbsDown: stats.down,
        total,
        thumbsUpPercent: total > 0 ? Math.round((stats.up / total) * 100) : 0
      };
    }).sort((a, b) => b.total - a.total);

    // Trend over time
    const now = new Date();
    const days = this.getDaysForRange();
    const trend: FeedbackTrendPoint[] = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);
      const nextDate = new Date(date);
      nextDate.setDate(nextDate.getDate() + 1);

      const dayRated = ratedMessages.filter(m => {
        const at = m.QualityFeedbackAt ? new Date(m.QualityFeedbackAt) : new Date(m.ReceivedAt);
        return at >= date && at < nextDate;
      });

      const up = dayRated.filter(m => m.QualityFeedback === 'Up').length;
      const down = dayRated.filter(m => m.QualityFeedback === 'Down').length;
      const total = up + down;

      trend.push({
        label: this.formatDateLabel(date),
        date,
        thumbsUp: up,
        thumbsDown: down,
        total,
        thumbsUpPercent: total > 0 ? Math.round((up / total) * 100) : 0
      });
    }

    // For large ranges, filter out empty days to avoid tiny bars and label overlap
    if (days > 30) {
      this.feedbackTrend = trend.filter(t => t.total > 0);
    } else {
      this.feedbackTrend = trend;
    }
    this.maxFeedbackTrendValue = Math.max(...this.feedbackTrend.map(t => t.total), 1);
    this.feedbackTrendMidpoint = Math.round(this.maxFeedbackTrendValue / 2);
  }

  private getDaysForRange(): number {
    switch (this.selectedTimeRange) {
      case 'today': return 1;
      case '7d': return 7;
      case '30d': return 30;
      case '90d': return 90;
      case 'custom':
        if (this.customStartDate && this.customEndDate) {
          const start = new Date(this.customStartDate + 'T00:00:00');
          const end = new Date(this.customEndDate + 'T00:00:00');
          return Math.max(Math.ceil((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 1, 1);
        }
        return 7;
      case 'all': return this.getAllTimeDays();
    }
  }

  private getAllTimeDays(): number {
    if (this.allMessages.length === 0) return 30;
    const dates = this.allMessages.map(m => new Date(m.ReceivedAt).getTime());
    const earliest = Math.min(...dates);
    const now = new Date().getTime();
    const days = Math.ceil((now - earliest) / (24 * 60 * 60 * 1000)) + 1;
    return Math.max(days, 1);
  }

  private formatDateLabel(date: Date): string {
    const days = this.getDaysForRange();
    if (days <= 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else if (days <= 30) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  }

  private computeGenerationStatusBreakdown(messages: IzzyChannelMessageEntity[]): void {
    const statusColors: Record<string, string> = {
      'Read': 'var(--mj-text-disabled)',
      'Creating Reply': 'var(--izzy-info)',
      'Generated': 'var(--izzy-success)',
      'Skipped': 'var(--izzy-warning)',
      'Failed': 'var(--izzy-error)'
    };

    const statusCounts = new Map<string, number>();
    messages.forEach(m => {
      const status = m.GenerationStatus || 'Unknown';
      statusCounts.set(status, (statusCounts.get(status) || 0) + 1);
    });

    const total = messages.length || 1;
    this.generationStatusBreakdown = Array.from(statusCounts.entries())
      .map(([status, count]) => {
        const pct = (count / total) * 100;
        const rounded = Math.round(pct);
        return {
          status,
          count,
          percentage: rounded,
          displayPercentage: count > 0 && rounded === 0 ? '<1%' : `${rounded}%`,
          color: statusColors[status] || 'var(--mj-text-muted)'
        };
      })
      .sort((a, b) => b.count - a.count);
  }

  private computeApprovalStatusBreakdown(messages: IzzyChannelMessageEntity[]): void {
    const statusColors: Record<string, string> = {
      'Not Required': 'var(--mj-text-disabled)',
      'Pending': 'var(--izzy-warning)',
      'Approved': 'var(--izzy-success)',
      'Rejected': 'var(--izzy-error)'
    };

    const statusCounts = new Map<string, number>();
    messages.forEach(m => {
      const status = m.ApprovalStatus || 'Unknown';
      statusCounts.set(status, (statusCounts.get(status) || 0) + 1);
    });

    const total = messages.length || 1;
    this.approvalStatusBreakdown = Array.from(statusCounts.entries())
      .map(([status, count]) => {
        const pct = (count / total) * 100;
        const rounded = Math.round(pct);
        return {
          status,
          count,
          percentage: rounded,
          displayPercentage: count > 0 && rounded === 0 ? '<1%' : `${rounded}%`,
          color: statusColors[status] || 'var(--mj-text-muted)'
        };
      })
      .sort((a, b) => b.count - a.count);
  }

  private computeChannelPerformance(messages: IzzyChannelMessageEntity[]): void {
    const channelStats = new Map<string, {
      total: number;
      generated: number;
      failed: number;
      pending: number;
      responseTimes: number[];
    }>();

    messages.forEach(m => {
      const channelId = m.ChannelID;
      if (!channelStats.has(channelId)) {
        channelStats.set(channelId, { total: 0, generated: 0, failed: 0, pending: 0, responseTimes: [] });
      }
      const stats = channelStats.get(channelId)!;
      stats.total++;

      if (m.GenerationStatus === 'Generated') {
        stats.generated++;

        // Calculate response time
        const receivedAt = new Date(m.ReceivedAt);
        const generatedAt = m.GeneratedAt ? new Date(m.GeneratedAt) : null;
        if (generatedAt) {
          const diffMinutes = (generatedAt.getTime() - receivedAt.getTime()) / 60000;
          if (diffMinutes >= 0) {
            stats.responseTimes.push(diffMinutes);
          }
        }
      }
      if (m.GenerationStatus === 'Failed') stats.failed++;
      if (m.GenerationStatus === 'Creating Reply' || m.GenerationStatus === 'Read') stats.pending++;
    });

    this.channelPerformance = Array.from(channelStats.entries())
      .map(([channelId, stats]) => {
        const channel = this.channels.find(c => c.ID === channelId);
        const avgResponseTime = stats.responseTimes.length > 0
          ? stats.responseTimes.reduce((a, b) => a + b, 0) / stats.responseTimes.length
          : 0;

        return {
          channelId,
          channelName: channel?.Name || 'Unknown Channel',
          totalMessages: stats.total,
          generated: stats.generated,
          failed: stats.failed,
          pending: stats.pending,
          avgResponseTime,
          successRate: stats.total > 0 ? Math.round((stats.generated / stats.total) * 100) : 0
        };
      })
      .sort((a, b) => b.totalMessages - a.totalMessages);
  }

  /**
   * Compute top senders
   */
  private computeTopSenders(messages: IzzyChannelMessageEntity[]): void {
    const senderCounts = new Map<string, number>();

    messages.forEach(m => {
      const sender = m.Sender || 'Unknown';
      senderCounts.set(sender, (senderCounts.get(sender) || 0) + 1);
    });

    const total = messages.length || 1;
    this.topSenders = Array.from(senderCounts.entries())
      .map(([sender, count]) => ({
        sender,
        count,
        percentage: Math.round((count / total) * 100)
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }

  /**
   * Compute heatmap data for peak hours
   */
  private computeHeatmapData(messages: IzzyChannelMessageEntity[]): void {
    const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const heatmap: HeatmapCell[] = [];

    // Initialize all cells to 0
    for (let day = 0; day < 7; day++) {
      for (let hour = 0; hour < 24; hour++) {
        heatmap.push({
          day,
          hour,
          count: 0,
          dayLabel: dayLabels[day],
          hourLabel: hour === 0 ? '12am' : hour < 12 ? `${hour}am` : hour === 12 ? '12pm' : `${hour - 12}pm`
        });
      }
    }

    // Count messages per day/hour
    messages.forEach(m => {
      const date = new Date(m.ReceivedAt);
      const day = date.getDay();
      const hour = date.getHours();
      const cell = heatmap.find(c => c.day === day && c.hour === hour);
      if (cell) {
        cell.count++;
      }
    });

    this.heatmapData = heatmap;
    this.maxHeatmapValue = Math.max(...heatmap.map(c => c.count), 1);
  }

  // ========== Chart Rendering ==========

  renderLineChart(): void {
    if (!this.lineChartContainer?.nativeElement || this.messageVolumeTrend.length === 0) {
      return;
    }

    const container = this.lineChartContainer.nativeElement;
    const data = this.messageVolumeTrend;

    // D3 sets SVG presentation attributes, which can't resolve var() — read the
    // computed token values so series colors follow the theme (incl. dark mode).
    const seriesTotal = this.cssToken('--izzy-chart-1', '#416CA8');
    const seriesResponse = this.cssToken('--izzy-chart-2', '#B15A3C');
    const seriesApproved = this.cssToken('--izzy-success', '#4C7357');
    const gridStroke = this.cssToken('--mj-border-default', '#e0e0e0');
    const dotRing = this.cssToken('--mj-bg-surface', '#ffffff');

    d3.select(container).selectAll('*').remove();

    const margin = { top: 20, right: this.chartSeries.responseTime ? 60 : 30, bottom: 40, left: 50 };
    const width = container.clientWidth - margin.left - margin.right;
    const height = 200 - margin.top - margin.bottom;

    const svg = d3.select(container)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Use index-based keys to avoid duplicate date labels collapsing in scalePoint
    const domainKeys = data.map((_, i) => String(i));
    const x = d3.scalePoint<string>()
      .domain(domainKeys)
      .range([0, width])
      .padding(0.1);

    const yMessages = d3.scaleLinear()
      .domain([0, Math.max(d3.max(data, d => d.value) || 1, 1) * 1.1])
      .range([height, 0]);

    const maxResponseTime = Math.max(d3.max(data, d => d.avgResponseTime) || 1, 1) * 1.2;
    const yTime = d3.scaleLinear()
      .domain([0, maxResponseTime])
      .range([height, 0]);

    const defs = svg.append('defs');

    const totalGradient = defs.append('linearGradient')
      .attr('id', 'totalAreaGradient')
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '0%').attr('y2', '100%');

    totalGradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', seriesTotal)
      .attr('stop-opacity', 0.3);

    totalGradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', seriesTotal)
      .attr('stop-opacity', 0.05);

    const approvedGradient = defs.append('linearGradient')
      .attr('id', 'approvedAreaGradient')
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '0%').attr('y2', '100%');

    approvedGradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', seriesApproved)
      .attr('stop-opacity', 0.3);

    approvedGradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', seriesApproved)
      .attr('stop-opacity', 0.05);

    svg.append('g')
      .attr('class', 'd3-grid')
      .selectAll('line')
      .data(yMessages.ticks(4))
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', d => yMessages(d))
      .attr('y2', d => yMessages(d))
      .attr('stroke', gridStroke)
      .attr('stroke-dasharray', '3,3');

    const totalArea = d3.area<ChartDataPoint>()
      .x((d, i) => x(String(i)) || 0)
      .y0(height)
      .y1(d => yMessages(d.value))
      .curve(d3.curveMonotoneX);

    const approvedArea = d3.area<ChartDataPoint>()
      .x((d, i) => x(String(i)) || 0)
      .y0(height)
      .y1(d => yMessages(d.approved))
      .curve(d3.curveMonotoneX);

    const totalLine = d3.line<ChartDataPoint>()
      .x((d, i) => x(String(i)) || 0)
      .y(d => yMessages(d.value))
      .curve(d3.curveMonotoneX);

    const approvedLine = d3.line<ChartDataPoint>()
      .x((d, i) => x(String(i)) || 0)
      .y(d => yMessages(d.approved))
      .curve(d3.curveMonotoneX);

    const responseTimeLine = d3.line<ChartDataPoint>()
      .defined(d => d.avgResponseTime > 0)
      .x((d, i) => x(String(i)) || 0)
      .y(d => yTime(d.avgResponseTime))
      .curve(d3.curveMonotoneX);

    if (this.chartSeries.total) {
      svg.append('path')
        .datum(data)
        .attr('class', 'd3-area-total')
        .attr('fill', 'url(#totalAreaGradient)')
        .attr('d', totalArea);

      svg.append('path')
        .datum(data)
        .attr('class', 'd3-line-total')
        .attr('fill', 'none')
        .attr('stroke', seriesTotal)
        .attr('stroke-width', 2.5)
        .attr('d', totalLine);

      // Only show dots when data points are sparse enough to be distinct
      if (data.length <= 30) {
        svg.selectAll('.d3-dot-total')
          .data(data)
          .enter()
          .append('circle')
          .attr('class', 'd3-dot-total')
          .attr('cx', (d, i) => x(String(i)) || 0)
          .attr('cy', d => yMessages(d.value))
          .attr('r', 4)
          .attr('fill', seriesTotal)
          .attr('stroke', dotRing)
          .attr('stroke-width', 2);
      }
    }

    if (this.chartSeries.approved) {
      svg.append('path')
        .datum(data)
        .attr('class', 'd3-area-approved')
        .attr('fill', 'url(#approvedAreaGradient)')
        .attr('d', approvedArea);

      svg.append('path')
        .datum(data)
        .attr('class', 'd3-line-approved')
        .attr('fill', 'none')
        .attr('stroke', seriesApproved)
        .attr('stroke-width', 2.5)
        .attr('d', approvedLine);

      if (data.length <= 30) {
        svg.selectAll('.d3-dot-approved')
          .data(data)
          .enter()
          .append('circle')
          .attr('class', 'd3-dot-approved')
          .attr('cx', (d, i) => x(String(i)) || 0)
          .attr('cy', d => yMessages(d.approved))
          .attr('r', 4)
          .attr('fill', seriesApproved)
          .attr('stroke', dotRing)
          .attr('stroke-width', 2);
      }
    }

    if (this.chartSeries.responseTime) {
      const dataWithResponseTime = data.filter(d => d.avgResponseTime > 0);

      if (dataWithResponseTime.length > 0) {
        svg.append('path')
          .datum(data)
          .attr('class', 'd3-line-response-time')
          .attr('fill', 'none')
          .attr('stroke', seriesResponse)
          .attr('stroke-width', 2.5)
          .attr('stroke-dasharray', '6,3')
          .attr('d', responseTimeLine);

        if (data.length <= 30) {
          svg.selectAll('.d3-dot-response-time')
            .data(dataWithResponseTime)
            .enter()
            .append('circle')
            .attr('class', 'd3-dot-response-time')
            .attr('cx', d => x(String(data.indexOf(d))) || 0)
            .attr('cy', d => yTime(d.avgResponseTime))
            .attr('r', 4)
            .attr('fill', seriesResponse)
            .attr('stroke', dotRing)
            .attr('stroke-width', 2);
        }
      }
    }

    // Thin out x-axis labels to avoid overlap
    const maxLabels = Math.floor(width / 60);
    const tickInterval = maxLabels > 0 ? Math.ceil(data.length / maxLabels) : 1;
    const tickKeys = domainKeys.filter((_, i) => i % tickInterval === 0);

    const xAxis = svg.append('g')
      .attr('class', 'd3-x-axis')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x).tickValues(tickKeys).tickFormat((d) => data[Number(d)]?.label || ''));

    xAxis.selectAll('text')
      .attr('fill', this.cssToken('--mj-text-muted', '#666'))
      .attr('font-size', '11px');

    xAxis.select('.domain').attr('stroke', this.cssToken('--mj-border-default', '#ccc'));
    xAxis.selectAll('.tick line').attr('stroke', this.cssToken('--mj-border-default', '#ccc'));

    const yAxisLeft = svg.append('g')
      .attr('class', 'd3-y-axis')
      .call(d3.axisLeft(yMessages).ticks(5).tickFormat(d => d3.format('.0f')(d as number)));

    yAxisLeft.selectAll('text')
      .attr('fill', this.cssToken('--mj-text-muted', '#666'))
      .attr('font-size', '11px');

    yAxisLeft.select('.domain').attr('stroke', this.cssToken('--mj-border-default', '#ccc'));
    yAxisLeft.selectAll('.tick line').attr('stroke', this.cssToken('--mj-border-default', '#ccc'));

    if (this.chartSeries.responseTime) {
      const yAxisRight = svg.append('g')
        .attr('class', 'd3-y-axis-right')
        .attr('transform', `translate(${width}, 0)`)
        .call(d3.axisRight(yTime).ticks(5).tickFormat(d => {
          const mins = d as number;
          if (mins < 1) return `${Math.round(mins * 60)}s`;
          if (mins < 60) return `${Math.round(mins)}m`;
          return `${(mins / 60).toFixed(1)}h`;
        }));

      yAxisRight.selectAll('text')
        .attr('fill', seriesResponse)
        .attr('font-size', '11px');

      yAxisRight.select('.domain').attr('stroke', seriesResponse);
      yAxisRight.selectAll('.tick line').attr('stroke', seriesResponse);
    }

    const tooltip = d3.select(container)
      .append('div')
      .attr('class', 'd3-tooltip')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('background', 'white')
      .style('border', '1px solid #e0e0e0')
      .style('border-radius', '8px')
      .style('padding', '10px 14px')
      .style('box-shadow', '0 4px 12px rgba(0,0,0,0.15)')
      .style('pointer-events', 'none')
      .style('font-size', '12px')
      .style('z-index', '10000');

    svg.append('rect')
      .attr('class', 'd3-overlay')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', 'transparent')
      .on('mousemove', (event: MouseEvent) => {
        const [mouseX] = d3.pointer(event);
        const xDomain = x.domain();
        const step = width / (xDomain.length - 1 || 1);
        const idx = Math.round(mouseX / step);
        const clampedIdx = Math.max(0, Math.min(idx, data.length - 1));
        const d = data[clampedIdx];

        if (d) {
          svg.selectAll('.d3-dot-total, .d3-dot-approved, .d3-dot-response-time')
            .attr('r', function() {
              const dataIndex = data.indexOf(d3.select(this).datum() as ChartDataPoint);
              return dataIndex === clampedIdx ? 6 : 4;
            });

          let tooltipHtml = `<div style="font-weight: 600; margin-bottom: 6px; color: #333;">${d.label}</div>`;

          if (this.chartSeries.total) {
            tooltipHtml += `
              <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;">
                <span style="width: 10px; height: 10px; border-radius: 50%; background: #00897b;"></span>
                <span style="color: #666;">Total:</span>
                <span style="font-weight: 600; color: #00897b;">${d.value}</span>
              </div>`;
          }

          if (this.chartSeries.approved) {
            tooltipHtml += `
              <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;">
                <span style="width: 10px; height: 10px; border-radius: 50%; background: #4caf50;"></span>
                <span style="color: #666;">Approved:</span>
                <span style="font-weight: 600; color: #4caf50;">${d.approved}</span>
              </div>`;
          }

          if (this.chartSeries.responseTime && d.avgResponseTime > 0) {
            tooltipHtml += `
              <div style="display: flex; align-items: center; gap: 6px;">
                <span style="width: 10px; height: 10px; border-radius: 50%; background: #673ab7;"></span>
                <span style="color: #666;">Avg Response:</span>
                <span style="font-weight: 600; color: #673ab7;">${this.formatDuration(d.avgResponseTime)}</span>
              </div>`;
          }

          // Smart positioning: flip tooltip to left side if too close to right edge
          const xPos = x(String(clampedIdx)) || 0;
          const tooltipWidth = 180; // approximate tooltip width
          const containerWidth = container.clientWidth;
          const isNearRightEdge = (xPos + margin.left + tooltipWidth + 20) > containerWidth;

          const leftPos = isNearRightEdge
            ? `${xPos + margin.left - tooltipWidth - 15}px`
            : `${xPos + margin.left + 15}px`;

          tooltip
            .style('opacity', 1)
            .html(tooltipHtml)
            .style('left', leftPos)
            .style('top', `${yMessages(d.value) + margin.top - 10}px`);
        }
      })
      .on('mouseout', () => {
        svg.selectAll('.d3-dot-total, .d3-dot-approved, .d3-dot-response-time').attr('r', 4);
        tooltip.style('opacity', 0);
      })
      .on('click', (event: MouseEvent) => {
        const [mouseX] = d3.pointer(event);
        const xDomain = x.domain();
        const step = width / (xDomain.length - 1 || 1);
        const idx = Math.round(mouseX / step);
        const clampedIdx = Math.max(0, Math.min(idx, data.length - 1));
        const d = data[clampedIdx];

        if (d) {
          this.onBarClick(d, clampedIdx);
        }
      });

    this.chartRendered = true;
  }

  /**
   * Render peak hours heatmap
   */
  renderHeatmap(): void {
    if (!this.heatmapContainer?.nativeElement || this.heatmapData.length === 0) {
      return;
    }

    const container = this.heatmapContainer.nativeElement;
    d3.select(container).selectAll('*').remove();

    const margin = { top: 30, right: 20, bottom: 20, left: 50 };
    const cellSize = 24;
    const width = 24 * cellSize + margin.left + margin.right;
    const height = 7 * cellSize + margin.top + margin.bottom;

    const svg = d3.select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const hourLabels = ['12a', '', '', '3a', '', '', '6a', '', '', '9a', '', '', '12p', '', '', '3p', '', '', '6p', '', '', '9p', '', ''];

    const colorScale = d3.scaleSequential()
      .domain([0, this.maxHeatmapValue])
      .interpolator(d3.interpolateRgb(this.cssToken('--mj-color-brand-100', '#F1E0D6'), this.cssToken('--mj-color-brand-700', '#7E3D28')));

    // Draw cells
    svg.selectAll('.heatmap-cell')
      .data(this.heatmapData)
      .enter()
      .append('rect')
      .attr('class', 'heatmap-cell')
      .attr('x', d => d.hour * cellSize)
      .attr('y', d => d.day * cellSize)
      .attr('width', cellSize - 2)
      .attr('height', cellSize - 2)
      .attr('rx', 4)
      .attr('fill', d => d.count > 0 ? colorScale(d.count) : this.cssToken('--mj-bg-surface-sunken', '#f0f0f0'))
      .attr('stroke', this.cssToken('--mj-bg-surface', '#fff'))
      .attr('stroke-width', 1)
      .style('cursor', 'pointer')
      .on('click', (_event: MouseEvent, d: HeatmapCell) => {
        this.onHeatmapCellClick(d);
      })
      .append('title')
      .text(d => `${d.dayLabel} ${d.hourLabel}: ${d.count} messages`);

    // Add day labels
    svg.selectAll('.day-label')
      .data(dayLabels)
      .enter()
      .append('text')
      .attr('class', 'day-label')
      .attr('x', -8)
      .attr('y', (_, i) => i * cellSize + cellSize / 2 + 4)
      .attr('text-anchor', 'end')
      .attr('font-size', '11px')
      .attr('fill', this.cssToken('--mj-text-muted', '#666'))
      .text(d => d);

    // Add hour labels
    svg.selectAll('.hour-label')
      .data(hourLabels)
      .enter()
      .append('text')
      .attr('class', 'hour-label')
      .attr('x', (_, i) => i * cellSize + cellSize / 2)
      .attr('y', -8)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', this.cssToken('--mj-text-muted', '#666'))
      .text(d => d);
  }

  /**
   * Render channel comparison bar chart
   */
  renderChannelComparisonChart(): void {
    if (!this.channelComparisonContainer?.nativeElement || this.channelPerformance.length === 0) {
      return;
    }

    const container = this.channelComparisonContainer.nativeElement;
    const seriesApproved = this.cssToken('--izzy-success', '#4C7357');
    d3.select(container).selectAll('*').remove();

    const data = this.channelPerformance.slice(0, 8); // Top 8 channels
    const margin = { top: 20, right: 120, bottom: 40, left: 140 };
    const barHeight = 28;
    const height = data.length * barHeight + margin.top + margin.bottom;
    const width = container.clientWidth - margin.left - margin.right;

    const svg = d3.select(container)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const maxValue = Math.max(...data.map(d => d.totalMessages), 1);

    const x = d3.scaleLinear()
      .domain([0, maxValue])
      .range([0, width]);

    const y = d3.scaleBand()
      .domain(data.map(d => d.channelName))
      .range([0, data.length * barHeight])
      .padding(0.2);

    // Background bars (total)
    svg.selectAll('.bar-total')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'bar-total')
      .attr('x', 0)
      .attr('y', d => y(d.channelName) || 0)
      .attr('width', d => x(d.totalMessages))
      .attr('height', y.bandwidth())
      .attr('fill', this.cssToken('--mj-bg-surface-sunken', '#e0e0e0'))
      .attr('rx', 4);

    // Generated bars
    svg.selectAll('.bar-generated')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'bar-generated')
      .attr('x', 0)
      .attr('y', d => y(d.channelName) || 0)
      .attr('width', d => x(d.generated))
      .attr('height', y.bandwidth())
      .attr('fill', seriesApproved)
      .attr('rx', 4);

    // Channel names
    svg.selectAll('.channel-label')
      .data(data)
      .enter()
      .append('text')
      .attr('class', 'channel-label')
      .attr('x', -8)
      .attr('y', d => (y(d.channelName) || 0) + y.bandwidth() / 2 + 4)
      .attr('text-anchor', 'end')
      .attr('font-size', '12px')
      .attr('fill', this.cssToken('--mj-text-primary', '#333'))
      .text(d => d.channelName.length > 18 ? d.channelName.substring(0, 18) + '...' : d.channelName);

    // Values
    svg.selectAll('.value-label')
      .data(data)
      .enter()
      .append('text')
      .attr('class', 'value-label')
      .attr('x', d => x(d.totalMessages) + 8)
      .attr('y', d => (y(d.channelName) || 0) + y.bandwidth() / 2 + 4)
      .attr('font-size', '11px')
      .attr('fill', this.cssToken('--mj-text-muted', '#666'))
      .text(d => `${d.generated}/${d.totalMessages} (${d.successRate}%)`);
  }

  // ========== Tickets section charts (§13) ==========

  /** Render the Tickets-section breakdown charts (by status/type/channel). The per-assignee
   *  workload is a static HTML table (ticketAssignmentSummaries), not a D3 chart. */
  renderTicketCharts(): void {
    this.renderHorizontalBars(this.ticketStatusContainer, this.ticketsByStatus, this.cssToken('--izzy-chart-1', '#416CA8'));
    this.renderHorizontalBars(this.ticketTypeContainer, this.ticketsByType, this.cssToken('--izzy-chart-2', '#B15A3C'));
    this.renderHorizontalBars(this.ticketChannelContainer, this.ticketsByChannel, this.cssToken('--izzy-chart-3', '#6B4FA3'));
  }

  /**
   * Reusable horizontal bar chart for a category→count breakdown (mirrors the channel-comparison
   * chart's D3 approach). Renders top 8 rows by value with labels + value annotations.
   */
  private renderHorizontalBars(ref: ElementRef<HTMLDivElement> | undefined, rows: TicketBreakdown[], color: string): void {
    const container = ref?.nativeElement;
    if (!container) return;
    d3.select(container).selectAll('*').remove();
    if (rows.length === 0) {
      d3.select(container).append('div')
        .attr('class', 'chart-empty')
        .style('color', this.cssToken('--mj-text-muted', '#888')).style('font-size', '13px').style('padding', '16px')
        .text('No data for this period.');
      return;
    }

    const data = rows.slice(0, 8);
    const margin = { top: 12, right: 56, bottom: 12, left: 150 };
    const barHeight = 28;
    const height = data.length * barHeight + margin.top + margin.bottom;
    const width = Math.max(container.clientWidth - margin.left - margin.right, 80);

    const svg = d3.select(container)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const maxValue = Math.max(...data.map(d => d.value), 1);
    const x = d3.scaleLinear().domain([0, maxValue]).range([0, width]);
    const y = d3.scaleBand().domain(data.map(d => d.label)).range([0, data.length * barHeight]).padding(0.2);

    svg.selectAll('.bar-bg').data(data).enter().append('rect')
      .attr('x', 0).attr('y', d => y(d.label) || 0)
      .attr('width', width).attr('height', y.bandwidth())
      .attr('fill', this.cssToken('--mj-bg-surface-sunken', '#eef0f4')).attr('rx', 4);

    svg.selectAll('.bar').data(data).enter().append('rect')
      .attr('x', 0).attr('y', d => y(d.label) || 0)
      .attr('width', d => x(d.value)).attr('height', y.bandwidth())
      .attr('fill', color).attr('rx', 4);

    svg.selectAll('.cat-label').data(data).enter().append('text')
      .attr('x', -8).attr('y', d => (y(d.label) || 0) + y.bandwidth() / 2 + 4)
      .attr('text-anchor', 'end').attr('font-size', '12px').attr('fill', this.cssToken('--mj-text-primary', '#333'))
      .text(d => d.label.length > 20 ? d.label.substring(0, 20) + '…' : d.label);

    svg.selectAll('.val-label').data(data).enter().append('text')
      .attr('x', d => x(d.value) + 8).attr('y', d => (y(d.label) || 0) + y.bandwidth() / 2 + 4)
      .attr('font-size', '11px').attr('fill', this.cssToken('--mj-text-muted', '#666'))
      .text(d => `${d.value}`);
  }

  // ========== Export Methods ==========

  /**
   * Export current view data to CSV
   */
  exportToCSV(): void {
    const filteredMessages = this.filterMessagesByTimeAndOrg(this.allMessages);
    const rows: string[] = [];

    // Header
    rows.push('Sender,Subject,Channel,Received At,Generation Status,Approval Status,Response Time (min)');

    // Data rows
    filteredMessages.forEach(m => {
      const channel = this.channels.find(c => c.ID === m.ChannelID);
      const receivedAt = new Date(m.ReceivedAt);
      const generatedAt = m.GeneratedAt ? new Date(m.GeneratedAt) : null;
      const responseTime = generatedAt
        ? ((generatedAt.getTime() - receivedAt.getTime()) / 60000).toFixed(2)
        : '';

      rows.push([
        `"${(m.Sender || '').replace(/"/g, '""')}"`,
        `"${(m.Subject || '').replace(/"/g, '""')}"`,
        `"${(channel?.Name || '').replace(/"/g, '""')}"`,
        receivedAt.toISOString(),
        m.GenerationStatus || '',
        m.ApprovalStatus || '',
        responseTime
      ].join(','));
    });

    // Download
    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `analytics-export-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // ========== Utility Methods ==========

  formatNumber(num: number): string {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  }

  getBarHeight(value: number): number {
    return this.maxMessageVolume > 0 ? (value / this.maxMessageVolume) * 100 : 0;
  }

  /** Computed value of a CSS custom property — D3 presentation attributes can't resolve var(). */
  private cssToken(name: string, fallback: string): string {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  }

  getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      'Generated': 'var(--izzy-success)',
      'Failed': 'var(--izzy-error)',
      'Creating Reply': 'var(--izzy-info)',
      'Read': 'var(--mj-text-disabled)',
      'Skipped': 'var(--izzy-warning)',
      'Pending': 'var(--izzy-warning)',
      'Approved': 'var(--izzy-success)',
      'Rejected': 'var(--izzy-error)',
      'Not Required': 'var(--mj-text-disabled)',
      'Completed': 'var(--izzy-success)',
      'Running': 'var(--izzy-info)',
      'Canceled': 'var(--izzy-warning)'
    };
    return colors[status] || 'var(--mj-text-muted)';
  }

  getTimeSince(date: Date): string {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return then.toLocaleDateString();
  }

  truncateText(text: string, maxLength: number): string {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  getHeatmapIntensity(count: number): string {
    if (count === 0) return 'var(--mj-bg-surface-sunken)';
    // Brand sequential ramp: one hue, light → dark (terracotta family).
    const intensity = Math.min(count / this.maxHeatmapValue, 1);
    const pct = Math.round(intensity * 100);
    return `color-mix(in srgb, var(--mj-color-brand-700) ${pct}%, var(--mj-color-brand-100))`;
  }

  /**
   * Get total message count for a specific day of the week
   */
  getDayCount(dayIndex: number): number {
    return this.heatmapData
      .filter(c => c.day === dayIndex)
      .reduce((sum, c) => sum + c.count, 0);
  }

  /**
   * Get percentage of messages for a day relative to max day
   */
  getDayPercentage(dayIndex: number): number {
    const dayCounts = [0, 1, 2, 3, 4, 5, 6].map(d => this.getDayCount(d));
    const maxCount = Math.max(...dayCounts, 1);
    return (this.getDayCount(dayIndex) / maxCount) * 100;
  }

  // ========== Drill-Down Methods ==========

  onKPIClick(kpi: KPICard): void {
    const filteredMessages = this.filterMessagesByTimeAndOrg(this.allMessages);
    let filterFn: (m: IzzyChannelMessageEntity) => boolean;

    switch (kpi.title) {
      case 'Total Messages':
        filterFn = () => true;
        break;
      case 'Replies Generated':
        filterFn = (m) => m.GenerationStatus === 'Generated';
        break;
      case 'Pending Approval':
        filterFn = (m) => m.ApprovalStatus === 'Pending';
        break;
      case 'Approved':
        filterFn = (m) => m.ApprovalStatus === 'Approved';
        break;
      case 'Rejected':
        filterFn = (m) => m.ApprovalStatus === 'Rejected';
        break;
      case 'BC: Channel Runs':
        filterFn = () => true;
        break;
      default:
        filterFn = () => true;
    }

    this.openDrillDown({
      type: 'kpi',
      title: kpi.title,
      value: String(kpi.value),
      icon: kpi.icon,
      color: kpi.color,
      filterFn
    }, filteredMessages);
  }

  onGenerationStatusClick(item: StatusBreakdown): void {
    const filteredMessages = this.filterMessagesByTimeAndOrg(this.allMessages);

    this.openDrillDown({
      type: 'generation-status',
      title: `Generation Status: ${item.status}`,
      value: `${item.count} messages (${item.percentage}%)`,
      icon: 'fa-robot',
      color: item.color,
      filterFn: (m) => m.GenerationStatus === item.status
    }, filteredMessages);
  }

  onApprovalStatusClick(item: StatusBreakdown): void {
    const filteredMessages = this.filterMessagesByTimeAndOrg(this.allMessages);

    this.openDrillDown({
      type: 'approval-status',
      title: `Approval Status: ${item.status}`,
      value: `${item.count} messages (${item.percentage}%)`,
      icon: 'fa-check-circle',
      color: item.color,
      filterFn: (m) => m.ApprovalStatus === item.status
    }, filteredMessages);
  }

  onChannelClick(channel: ChannelPerformance): void {
    const filteredMessages = this.filterMessagesByTimeAndOrg(this.allMessages);

    this.openDrillDown({
      type: 'channel',
      title: `Channel: ${channel.channelName}`,
      value: `${channel.totalMessages} messages, ${channel.successRate}% success rate`,
      icon: 'fa-broadcast-tower',
      color: 'var(--mj-brand-primary)',
      filterFn: (m) => m.ChannelID === channel.channelId
    }, filteredMessages);
  }

  getContactInitials(name: string): string {
    if (!name || name === 'Unassigned' || name === 'Unknown') return '?';
    const words = name.split(' ').filter(w => w.length > 0);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  onAssignmentClick(summary: AssignmentSummary): void {
    const filteredMessages = this.filterMessagesByTimeAndOrg(this.allMessages);

    // Filters mirror exactly what the clicked row counts in the current mode:
    // OPEN → that assignee's (or nobody's) pending/escalated queue;
    // HANDLED → that approver's approvals, or Izzy's unassigned approvals.
    const handled = this.workloadMode === 'handled';
    const filterFn = handled
      ? (summary.isIzzy
          ? (m: IzzyChannelMessageEntity) =>
              m.AIAutoApproved === true && m.ApprovalStatus === 'Approved'
          : summary.contactId
            ? (m: IzzyChannelMessageEntity) =>
                !m.AIAutoApproved && m.AssignedToPersonID === summary.contactId && m.ApprovalStatus === 'Approved'
            : (m: IzzyChannelMessageEntity) =>
                !m.AIAutoApproved && !m.AssignedToPersonID && m.ApprovalStatus === 'Approved')
      : (summary.contactId
          ? (m: IzzyChannelMessageEntity) =>
              m.AssignedToPersonID === summary.contactId &&
              (m.ApprovalStatus === 'Pending' || m.ApprovalStatus === 'Escalated')
          : (m: IzzyChannelMessageEntity) =>
              !m.AssignedToPersonID &&
              (m.ApprovalStatus === 'Pending' || m.ApprovalStatus === 'Escalated'));

    this.openDrillDown({
      type: 'kpi',
      title: summary.contactName,
      value: `${summary.count} active message(s)`,
      icon: summary.contactId ? 'fa-user' : 'fa-user-slash',
      color: summary.contactId ? 'var(--izzy-chart-3)' : 'var(--mj-text-disabled)',
      filterFn
    }, filteredMessages);
  }

  /**
   * Drill-in from the "Tickets by Assignee" workload row → open the Tickets workspace pre-filtered
   * to that assignee (or to Unassigned). Mirrors the message Assignments drill-down, but tickets live
   * in the queue (not the message drill-down modal), so we deep-link there via the nav service.
   */
  onTicketAssigneeClick(summary: TicketAssignmentSummary): void {
    // Pass via options.queryParams (4th arg), NOT configuration (2nd) — only queryParams flow through
    // the tab's reactive QueryParamChanged$/OnQueryParamsChanged channel, so the Tickets workspace
    // re-filters even when its tab is ALREADY OPEN (no page refresh needed).
    const queryParams: Record<string, string | null> = {
      assigneeFilter: summary.personId || 'unassigned'
    };
    if (this.selectedOrganizationId) {
      queryParams['orgId'] = this.selectedOrganizationId;
    }
    this.navigationService.OpenNavItemByName('Tickets', undefined, undefined, { queryParams });
  }

  onBarClick(point: ChartDataPoint, index: number): void {
    const filteredMessages = this.filterMessagesByTimeAndOrg(this.allMessages);
    const now = new Date();
    const days = this.getDaysForRange();

    const dayOffset = days - 1 - index;
    const barDate = new Date(now);
    barDate.setDate(barDate.getDate() - dayOffset);
    barDate.setHours(0, 0, 0, 0);

    const nextDate = new Date(barDate);
    nextDate.setDate(nextDate.getDate() + 1);

    const formattedDate = barDate.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    });

    this.openDrillDown({
      type: 'date',
      title: `Messages on ${formattedDate}`,
      value: `${point.value} messages`,
      icon: 'fa-calendar-day',
      color: 'var(--mj-brand-primary)',
      filterFn: (m) => {
        const receivedAt = new Date(m.ReceivedAt);
        return receivedAt >= barDate && receivedAt < nextDate;
      }
    }, filteredMessages);
  }

  onSenderClick(sender: TopSender): void {
    const filteredMessages = this.filterMessagesByTimeAndOrg(this.allMessages);

    this.openDrillDown({
      type: 'sender',
      title: `Sender: ${sender.sender}`,
      value: `${sender.count} messages (${sender.percentage}%)`,
      icon: 'fa-user',
      color: 'var(--izzy-chart-1)',
      filterFn: (m) => m.Sender === sender.sender
    }, filteredMessages);
  }

  onHeatmapCellClick(cell: HeatmapCell): void {
    const filteredMessages = this.filterMessagesByTimeAndOrg(this.allMessages);

    this.openDrillDown({
      type: 'heatmap',
      title: `${cell.dayLabel} at ${cell.hourLabel}`,
      value: `${cell.count} messages`,
      icon: 'fa-clock',
      color: 'var(--mj-brand-primary)',
      filterFn: (m) => {
        const date = new Date(m.ReceivedAt);
        return date.getDay() === cell.day && date.getHours() === cell.hour;
      }
    }, filteredMessages);
  }

  private openDrillDown(filter: DrillDownFilter, messages: IzzyChannelMessageEntity[]): void {
    this.drillDownFilter = filter;
    this.drillDownMessages = messages.filter(filter.filterFn);
    this.showDrillDown = true;
  }

  closeDrillDown(): void {
    this.showDrillDown = false;
    this.drillDownFilter = null;
    this.drillDownMessages = [];
  }

  getChannelName(channelId: string): string {
    const channel = this.channels.find(c => c.ID === channelId);
    return channel?.Name || 'Unknown Channel';
  }

  navigateToMessage(message: IzzyChannelMessageEntity): void {
    this.closeDrillDown();

    const channel = this.channels.find(c => c.ID === message.ChannelID);

    const queryParams: Record<string, string> = {
      messageId: message.ID,
      approvalStatus: message.ApprovalStatus || 'All',
      generationStatus: message.GenerationStatus || 'All'
    };

    if (channel) {
      queryParams['channelId'] = channel.ID;
      queryParams['organizationId'] = channel.OrganizationID;
    }

    this.navigationService.OpenNavItemByName('Messages', queryParams);
  }
}

/**
 * Analytics Resource Component - Top-level nav item
 */
@RegisterClass(BaseResourceComponent, 'IzzyAnalyticsResource')
@Component({
  standalone: false,
  selector: 'izzy-analytics-resource',
  template: `<izzy-analytics [navigationConfig]="Data?.Configuration"></izzy-analytics>`,
  styles: [`:host { display: block; height: 100%; }`]
})
export class IzzyAnalyticsResource extends BaseResourceComponent {
  async GetResourceDisplayName(_data: ResourceData): Promise<string> {
    return 'Analytics';
  }

  async GetResourceIconClass(_data: ResourceData): Promise<string> {
    return 'fa-solid fa-chart-line';
  }
}

export function LoadAnalyticsComponent(): void {
  // Intentionally empty - prevents tree-shaking
}
